import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { PlusIcon } from "../../assets/Icons/PlusIcon";
import { MinusIcon } from "../../assets/Icons/MinusIcon";
import { NavDownArrow } from "../../assets/Icons/NavArrowDown";
import NavArrowUp from "../../assets/Icons/NavArrowUp";
import OrderProgressStepper from "./OrderProgressStepper";
import { ORDER_DETAILS, ORDER_TABS } from "../../util/constants";
import { WarningCircle } from "../../assets/Icons/WarningCircle";
import {
  formatDateShort,
  formatDateForDisplay,
  formatDateForEditDisplay,
  formatDateForInput,
} from "../../util/dateUtil";
import {
  getFieldValidation,
  isContributorField,
  isValidDateValue,
  validateContributorUniqueness,
  CONTRIBUTOR_UNIQUE_FIELDS,
  isDateRelationshipField,
  validateDateRelationships,
  DATE_FIELDS,
} from "./fieldValidations.config";
import { useSelector } from "react-redux";
import { OrdersService, LookupService } from "../../api/services";
import { getApiErrorMessages } from "../../api/utils/errorHandler";
import { MotifSelect, MotifOption, MotifFormField } from "@ey-xd/motif-react";
import { LoadingIndicator } from "../CommonUIComponents/LoadingIndicator/LoadingIndicator";
import { CustomFieldsTooltip } from "../CommonUIComponents/CustomFieldsTooltip/CustomFieldsTooltip";
import { OutlookplusIcon } from "../../assets/Icons/OutlookplusIcon";
import { CalendarIcon } from "../../assets/Icons/Calendar";
import { ClockIcon } from "../../assets/Icons/ClockIcon";
import { EditIcon } from "../../assets/Icons/EditIcon";
import { InfoCircleIcon } from "../../assets/Icons/InfoCircleIcon";
import ActiveDirectoryUserSearch from "./ActiveDirectoryUserSearch";
import { OrderDetailsFileUpload } from "./OrderDetailsFileUpload";
import "./OrdersDetails.scss";
import { CustomTooltipBottom } from "../CommonUIComponents/CustomTooltip/CustomTooltipBottom";
import { CustomTooltipTop } from "../CommonUIComponents/CustomTooltip/CustomTooltipTop";
import { CustomTooltip } from "../CommonUIComponents/CustomTooltip/CustomTooltip";
import {
  MainDetailsTab,
  AdditionalDetailsTab,
  DatesTab,
  ContributorsTab,
  OtherTab,
  CheckListTab,
} from "./tabs";
import {
  // Main Tab Constants
  ORDER_DETAILS_MAIN_TABS,
  SECTION_TO_TAB_LABEL,
  // Location Lookup Constants
  LOCATION_LOOKUP_FIELD_CANDIDATES,
  APPLICATION_USED_LOOKUP_REGION_FIELD_CANDIDATES,
  // UI Constants
  ERROR_COLOR,
  EMPTY_DATE_PLACEHOLDER,
  FIELD_ERROR_TOOLTIP_MAX_WIDTH,
  FIELD_ERROR_TOOLTIP_VIEWPORT_PADDING,
  FIELD_ERROR_TOOLTIP_HIDPI_SCALE,
  INVALID_DATE_MESSAGE,
  DEFAULT_ORDER_UPDATE_ERROR_MESSAGE,
  VALIDATION_WARNINGS_TOAST_MESSAGE,
  ACTIVE_DIRECTORY_SELECTION_REQUIRED_MESSAGE,
  // Field Definition Constants
  ADDITIONAL_DID_FIELDS,
  TOOL_ISSUE_DESCRIPTION_TRIGGER_OPTION,
  REPROCESS_DESCRIPTION_TRIGGER_OPTION,
  OTHER_ERP_TRIGGER_OPTION,
  OTHER_EXTRACTION_TOOL_TRIGGER_OPTION,
  ANALYZER_TYPE_FIELD_NAMES,
  MARKET_SEGMENT_FIELD_NAMES,
  EFFORT_SECTION_NAME,
  SAVING_SECTION_NAME,
  CONTRIBUTOR_ROLE_FIELD_MAP,
  HIDDEN_CONTRIBUTOR_FIELD_LABELS,
  INTEGER_ONLY_FIELD_KEYS,
  NUMERIC_STEPPER_FIELD_KEYS,
  REGIONAL_LEAD_READ_ONLY_FIELD_NAMES,
  INVALID_DROPDOWN_OPTION_MESSAGE,
  FIELD_STATE_KEY_SEPARATOR,
  EFFORT_ROW_FIELD_CANDIDATES,
  EFFORT_PAYLOAD_FIELD_CANDIDATES,
  SAVING_ROW_FIELD_CANDIDATES,
  SAVING_PAYLOAD_FIELD_CANDIDATES,
  OTHER_SECTION_DISPLAY_NAMES,
  OTHER_SECTION_API_NAME_BY_DISPLAY_NAME,
  OTHER_SECTION_LAYOUT_PRIORITIES,
  OTHER_SECTION_HIDDEN_FIELD_CANDIDATES,
  NUMERIC_STEPPER_BLOCKED_KEYS,
  // Normalization Helpers
  isEmptyFieldValue,
  getInputTooltipTitle,
  normalizeFieldIdentifier,
  normalizeOptionLabel,
  normalizeLookupMatchValue,
  normalizeLocationLookupValue,
  normalizeListDisplayValue,
  normalizeIntegerFieldValue,
  // Field Type Check Functions
  isAdditionalDidField,
  isDateLikeField,
  isEmptyDateValue,
  isOrderComplexityField,
  isSavingOtherReasonField,
  isOtherOnHoldReasonField,
  isOnHoldReasonField,
  isOtherCleansingToolField,
  isOtherAnalyzerTypeField,
  isOtherMarketSegmentField,
  isToolIssueDescriptionField,
  isReprocessDescriptionField,
  isReprocessReasonField,
  isOtherErpField,
  isOtherExtractionToolField,
  isErpNameField,
  isExtractionToolField,
  isToolIssueTypeField,
  isCleansingToolField,
  isAnalyzerTypeField,
  isMarketSegmentField,
  isOtherCancellationReasonField,
  isCancellationReasonField,
  isOtherLikeOptionValue,
  isIntegerOnlyField,
  usesNumericStepperField,
  isManuallyEditableAutoPopulatedSectionField,
  // Trigger Value Check Functions
  isToolIssueDescriptionTriggerValue,
  isReprocessDescriptionTriggerValue,
  isOtherErpTriggerValue,
  isOtherExtractionToolTriggerValue,
  // Dropdown & Value Helper Functions
  getDropdownFieldOptionValue,
  getDropdownFieldOptionLabel,
  getDropdownFieldOptionMatchValues,
  doesDropdownOptionMatchValue,
  getKeyBackedDropdownOptions,
  getDropdownFieldOptions,
  doesDropdownValueMatchOptions,
  getDropdownDisplayMatchValue,
  getResolvedFieldValue,
  getAllowedNumericInputRegex,
  shouldPreventNumericStepperKey,
  shouldPreventNumericStepperPaste,
  // Field Matching & Candidate Functions
  getOrderField,
  getBaseFieldName,
  getFieldStateKey,
  findMatchingRowField,
  doesFieldMatchCandidates,
  doesFieldMatchAnyCandidateGroup,
  // Other Section Field Functions
  getOtherSectionFieldPriority,
  shouldHideOtherSectionField,
  isOtherSectionWideField,
  getOrderedOtherSectionFields,
  // Location Builder & Lookup Functions
  buildLocationDropdownOption,
  buildRegionLookupOption,
  buildSuperRegionLookupOption,
  buildCountryLookupOption,
  buildOfficeLookupOption,
  findMatchedLocationRecord,
  getLocationHierarchyRecords,
  // Application Used & Error Functions
  getApplicationUsedHoursReasonRegions,
  getApplicationUsedHoursReasonRegionMatchValues,
  getApplicationUsedHoursReasonMatchValues,
  buildApplicationUsedHoursReasonEntries,
  buildSavingReasonOptionsFromLookup,
  shouldApplySavingDefaultHours,
  formatSavingDefaultHoursValue,
  getUpdateOrderValidationWarnings,
  getApiErrorResponseData,
  getApiErrorDisplayMessage,
  // Row/Section Processing
  buildSectionRowKey,
  buildFieldStateKey,
  getRepeatedRowFields,
  getRowDataValueByCandidates,
  createNormalizedSectionRows,
} from "./orderDetailsFieldConstants";

// Error icon component to reduce duplication
const FieldErrorIcon = ({
  fieldKey,
  hasValidationError,
  getValidationErrorMessage,
  errorFieldArray,
}) => {
  const iconRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});

  const handleMouseEnter = useCallback(() => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const iconCenterX = rect.left + rect.width / 2;
      const tooltipScale =
        window.devicePixelRatio >= 1.5 ? FIELD_ERROR_TOOLTIP_HIDPI_SCALE : 1;
      const tooltipMaxWidth = FIELD_ERROR_TOOLTIP_MAX_WIDTH * tooltipScale;
      const availableWidth = Math.max(
        viewportWidth - FIELD_ERROR_TOOLTIP_VIEWPORT_PADDING * 2,
        0,
      );
      const safeHalfTooltipWidth = Math.max(
        Math.min(tooltipMaxWidth / 2, availableWidth / 2),
        0,
      );
      const clampedCenterX = Math.min(
        Math.max(
          iconCenterX,
          FIELD_ERROR_TOOLTIP_VIEWPORT_PADDING + safeHalfTooltipWidth,
        ),
        viewportWidth -
          FIELD_ERROR_TOOLTIP_VIEWPORT_PADDING -
          safeHalfTooltipWidth,
      );

      const style = {
        position: "fixed",
        top: rect.bottom + 8,
        left: clampedCenterX,
        transform:
          tooltipScale === 1
            ? "translateX(-50%)"
            : `translateX(-50%) scale(${tooltipScale})`,
        transformOrigin: "top center",
        maxWidth: `min(${tooltipMaxWidth}px, calc(100vw - ${FIELD_ERROR_TOOLTIP_VIEWPORT_PADDING * 2}px))`,
        zIndex: 999999,
      };

      setTooltipStyle(style);
      setShowTooltip(true);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  const errorMessage = hasValidationError(fieldKey)
    ? getValidationErrorMessage(fieldKey)
    : null;
  const hasError =
    hasValidationError(fieldKey) || errorFieldArray.includes(fieldKey);

  if (!hasError) return null;

  return (
    <>
      <span
        ref={iconRef}
        className="field-error-icon"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <InfoCircleIcon width="16" height="16" color={ERROR_COLOR} />
      </span>
      {showTooltip &&
        errorMessage &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="field-error-tooltip-fixed"
            role="tooltip"
            style={tooltipStyle}
          >
            {errorMessage}
          </div>,
          document.body,
        )}
    </>
  );
};

// Displays order details dashboard with editable fields and progress stepper
const OrdersDetailsDashboard = ({
  orderDetails: propOrderDetails,
  // onRefreshOrderDetails,
  onRefreshOrderDetails,
  onDataReceivedFlowChange,
  onFilesTabClick,
  requestedMainTab,
  isAILoading = false,
  onIncompleteData,
}) => {
  // Get current order from Redux store
  const { currentOrder, isLoading: isOrderLoading } = useSelector(
    (state) => state.orders,
  );

  // Get current user from Redux store for API calls
  const user = useSelector((state) => state.user?.user);
  const selectedRole = useSelector((state) => state.user?.selectedRole);
  const userEmail = user?.email || user?.mail || user?.username || "";

  // Match OrdersDashboard / SuperChatAI: role comes from selectedRole, with fallbacks
  const currentUserRole = useMemo(() => {
    const roleSource =
      selectedRole?.roleName ||
      selectedRole?.name ||
      (typeof selectedRole === "string" ? selectedRole : "") ||
      user?.role ||
      "";

    return String(roleSource).trim();
  }, [selectedRole, user?.role]);

  const normalizedCurrentUserRole = useMemo(
    () => currentUserRole.toUpperCase(),
    [currentUserRole],
  );

  const isDidRole = useMemo(
    () => !normalizedCurrentUserRole || normalizedCurrentUserRole === "DID",
    [normalizedCurrentUserRole],
  );

  const isRegionalLead = useMemo(() => {
    return normalizedCurrentUserRole === "REGIONAL LEAD";
  }, [normalizedCurrentUserRole]);

  const isApprover = useMemo(
    () => normalizedCurrentUserRole === "APPROVER",
    [normalizedCurrentUserRole],
  );

  const rawOrderDetails = currentOrder || propOrderDetails;
  const orderDetails = useMemo(
    () => (Array.isArray(rawOrderDetails) ? rawOrderDetails : []),
    [rawOrderDetails],
  );

  const isEditableFieldRegionalLead = useCallback(
    (fieldDetails) => {
      if (!fieldDetails) {
        return true;
      }

      const fieldName = String(
        fieldDetails?.field || fieldDetails?.baseFieldName || "",
      ).trim();

      //if (isRegionalLead && fieldName === "MercuryClientId") {
      // if (
      //   isRegionalLead &&
      //   REGIONAL_LEAD_READ_ONLY_FIELD_NAMES.includes(fieldName)
      // ) {
      //   return false;
      // }

      if (isManuallyEditableAutoPopulatedSectionField(fieldDetails)) {
        return true;
      }

      if (isRegionalLead) {
        if (typeof fieldDetails?.isEditableByRL === "boolean") {
          return fieldDetails.isEditableByRL;
        }

        return true;
      }

      if (isDidRole) {
        if (typeof fieldDetails?.isEditable === "boolean") {
          return fieldDetails.isEditable;
        }

        if (typeof fieldDetails?.editable === "boolean") {
          return fieldDetails.editable;
        }
      }

      return true;
    },
    [isDidRole, isRegionalLead],
  );

  // Memoized header data to avoid repetitive access
  const headerData = useMemo(
    () => orderDetails?.[0]?.headerSection?.[0] || {},
    [orderDetails],
  );

  // Check if order status is "Data Received" (case-insensitive)
  const isDataReceivedStatus = useMemo(() => {
    const currentStatus = headerData.currentSubStage || headerData.status || "";
    return currentStatus.toLowerCase() === "data received";
  }, [headerData]);

  // Contributor assignment is not mandatory while the order is in Order Validation
  const isOrderValidationStatus = useMemo(
    () =>
      String(headerData.currentSubStage || headerData.status || "")
        .trim()
        .toLowerCase() === "order validation",
    [headerData],
  );

  // Check if analyzer is "General Ledger Analyzer" (case-insensitive).
  // Files tab is only available for General Ledger Analyzer orders.
  const isGeneralLedgerAnalyzer = useMemo(() => {
    const analyzer = String(headerData.analyzer || "")
      .trim()
      .toLowerCase();
    return (
      analyzer === "general ledger analyzer" ||
      analyzer === "general ledger analyzer (sql)"
    );
  }, [headerData]);

  // Check if order is active (controls edit, chat, and quick actions)
  const isActiveOrder = useMemo(() => {
    return headerData.activeOrder === true;
  }, [headerData]);

  const checklistRole = useMemo(() => {
    if (isRegionalLead) {
      return "Regional Lead";
    }

    if (isApprover) {
      return "Approver";
    }

    const normalizedUserEmail = String(userEmail || "")
      .trim()
      .toLowerCase();

    if (!normalizedUserEmail) {
      return "";
    }

    const contributorValues = new Map();
    orderDetails.forEach((section) => {
      (section?.pagefields || []).forEach((field) => {
        const fieldName = String(
          field?.field || field?.fieldName || field?.baseFieldName || "",
        )
          .trim()
          .toLowerCase();
        if (
          fieldName === "preparerid" ||
          fieldName === "reviewerid" ||
          fieldName === "approverid"
        ) {
          const value = field?.fieldValue ?? field?.value ?? "";
          contributorValues.set(fieldName, String(value));
        }
      });
    });

    const includesUserEmail = (value) =>
      String(value || "")
        .split(",")
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean)
        .includes(normalizedUserEmail);

    if (includesUserEmail(contributorValues.get("preparerid"))) {
      return "Preparer";
    }

    if (includesUserEmail(contributorValues.get("reviewerid"))) {
      return "Reviewer";
    }

    if (includesUserEmail(contributorValues.get("approverid"))) {
      return "Approver";
    }

    return "";
  }, [isApprover, isRegionalLead, orderDetails, userEmail]);

  const checklistStage = useMemo(() => {
    const stage = String(
      headerData.currentSubStage || headerData.status || "",
    ).trim();
    const normalizedStage = stage.toLowerCase();
    const excludedStages = new Set([
      "order validation",
      "assigned request",
      "data received",
      "data received incomplete",
    ]);

    if (!normalizedStage || excludedStages.has(normalizedStage)) {
      return "";
    }

    if (
      normalizedStage.includes("analyzer & memo") ||
      normalizedStage.includes("analyser & memo")
    ) {
      return "Analyzer";
    }

    if (normalizedStage.includes("preparation & completeness")) {
      return "Completeness";
    }

    return stage;
  }, [headerData]);

  const isChecklistEnabled = Boolean(checklistRole && checklistStage);
  const checklistAccordionTitle = isChecklistEnabled
    ? `${checklistStage} - ${checklistRole}`
    : "";

  // Ref to track if component is mounted (for async cleanup)
  const isMountedRef = useRef(true);
  const dateInputRefs = useRef(new Map());
  const dropdownFieldRefs = useRef(new Map());
  const checklistRef = useRef(null);
  const checklistResetRef = useRef(null);
  const nextDraftOtherSectionRowIdRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [activeMainTab, setActiveMainTab] = useState(
    ORDER_DETAILS_MAIN_TABS.ORDER_INFO,
  );
  const [activeTab, setActiveTab] = useState(ORDER_TABS.MAIN_DETAILS);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldValues, setFieldValues] = useState([]);
  const [showAllTabs, setShowAllTabs] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [errorFieldArray, setErrorFieldArray] = useState([]);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [isAnalyzerTypeChanged, setIsAnalyzerTypeChanged] = useState(false);
  const [showAnalyzerTypeToast, setShowAnalyzerTypeToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState(
    DEFAULT_ORDER_UPDATE_ERROR_MESSAGE,
  );
  const [errorToastDetails, setErrorToastDetails] = useState([]);
  const [fieldValidationErrors, setFieldValidationErrors] = useState({});
  const [dropdownPlacementByFieldKey, setDropdownPlacementByFieldKey] =
    useState({});
  const [pendingContributorSelections, setPendingContributorSelections] =
    useState({});
  const [savingReasonOptionsByRow, setSavingReasonOptionsByRow] = useState({});
  const [savingsApplicationsResponse, setSavingsApplicationsResponse] =
    useState(null);
  const [
    applicationUsedHoursReasonLookupResponse,
    setApplicationUsedHoursReasonLookupResponse,
  ] = useState(null);
  const [locationHierarchyLookupResponse, setLocationHierarchyLookupResponse] =
    useState(null);
  const [draftOtherSectionRows, setDraftOtherSectionRows] = useState({});
  const [removedOtherSectionRowKeys, setRemovedOtherSectionRowKeys] =
    useState([]);

  const clearErrorToast = useCallback(() => {
    setShowErrorToast(false);
    setErrorToastMessage(DEFAULT_ORDER_UPDATE_ERROR_MESSAGE);
    setErrorToastDetails([]);
  }, []);

  const setDateInputRef = useCallback((fieldKey, element) => {
    if (!fieldKey) {
      return;
    }

    if (element) {
      dateInputRefs.current.set(fieldKey, element);
      return;
    }

    dateInputRefs.current.delete(fieldKey);
  }, []);

  const setDropdownFieldRef = useCallback((fieldKey, element) => {
    if (!fieldKey) {
      return;
    }

    if (element) {
      dropdownFieldRefs.current.set(fieldKey, element);
      return;
    }

    dropdownFieldRefs.current.delete(fieldKey);
  }, []);

  const getDropdownVisibleBounds = useCallback((wrapperElement) => {
    const scrollContainer =
      wrapperElement?.closest(".order-info-sections-scroll") ||
      wrapperElement?.closest(".orders-left-section");

    if (!scrollContainer) {
      return {
        top: 0,
        bottom: window.innerHeight,
      };
    }

    const containerRect = scrollContainer.getBoundingClientRect();

    return {
      top: Math.max(containerRect.top, 0),
      bottom: Math.min(containerRect.bottom, window.innerHeight),
    };
  }, []);

  const updateDropdownPlacement = useCallback(
    (fieldKey, optionCount = 0) => {
      if (!fieldKey) {
        return;
      }

      const wrapperElement = dropdownFieldRefs.current.get(fieldKey);

      if (!wrapperElement) {
        return;
      }

      const triggerElement =
        wrapperElement.querySelector(".motif-select__trigger-button") ||
        wrapperElement.querySelector(".motif-select-input") ||
        wrapperElement;
      const triggerRect = triggerElement.getBoundingClientRect();
      const { top, bottom } = getDropdownVisibleBounds(wrapperElement);
      const availableBelow = Math.max(bottom - triggerRect.bottom, 0);
      const availableAbove = Math.max(triggerRect.top - top, 0);
      const estimatedMenuHeight = Math.min(
        220,
        Math.max(112, optionCount * 34 + 20),
      );
      const shouldOpenUpward =
        availableBelow < estimatedMenuHeight &&
        availableAbove > availableBelow &&
        availableAbove >= 112;
      const nextPlacement = shouldOpenUpward ? "top" : "bottom";

      setDropdownPlacementByFieldKey((previousPlacements) => {
        if (previousPlacements[fieldKey] === nextPlacement) {
          return previousPlacements;
        }

        return {
          ...previousPlacements,
          [fieldKey]: nextPlacement,
        };
      });
    },
    [getDropdownVisibleBounds],
  );

  const getDateValidationError = useCallback((fieldKey, value) => {
    const dateInputElement = dateInputRefs.current.get(fieldKey);

    if (dateInputElement?.validity?.badInput) {
      return INVALID_DATE_MESSAGE;
    }

    const candidateValues = [dateInputElement?.value, String(value ?? "")]
      .map((candidateValue) => String(candidateValue ?? ""))
      .filter((candidateValue) => !isEmptyFieldValue(candidateValue));

    if (candidateValues.length === 0) {
      return "";
    }

    return candidateValues.every((candidateValue) =>
      isValidDateValue(candidateValue),
    )
      ? ""
      : INVALID_DATE_MESSAGE;
  }, []);

  useEffect(() => {
    setDraftOtherSectionRows({});
    setRemovedOtherSectionRowKeys([]);
    nextDraftOtherSectionRowIdRef.current = 0;
  }, [orderDetails]);

  const hasDependentLocationHierarchyFields = useMemo(() => {
    for (const section of orderDetails || []) {
      const pageFields = Array.isArray(section?.pagefields)
        ? section.pagefields
        : [];

      if (
        pageFields.some((field) =>
          doesFieldMatchAnyCandidateGroup(field, [
            LOCATION_LOOKUP_FIELD_CANDIDATES.superRegion,
            LOCATION_LOOKUP_FIELD_CANDIDATES.country,
            LOCATION_LOOKUP_FIELD_CANDIDATES.office,
          ]),
        )
      ) {
        return true;
      }
    }

    return false;
  }, [orderDetails]);

  const doesFieldMatchLocationCandidates = useCallback(
    (field, candidates = []) => {
      if (doesFieldMatchCandidates(field, candidates)) {
        return true;
      }

      if (candidates !== LOCATION_LOOKUP_FIELD_CANDIDATES.area) {
        return false;
      }

      return (
        hasDependentLocationHierarchyFields &&
        doesFieldMatchCandidates(field, ["Region"])
      );
    },
    [hasDependentLocationHierarchyFields],
  );

  const hasLocationHierarchyFields = useMemo(() => {
    for (const section of orderDetails || []) {
      const pageFields = Array.isArray(section?.pagefields)
        ? section.pagefields
        : [];

      if (
        pageFields.some(
          (field) =>
            doesFieldMatchLocationCandidates(
              field,
              LOCATION_LOOKUP_FIELD_CANDIDATES.area,
            ) ||
            doesFieldMatchAnyCandidateGroup(field, [
              LOCATION_LOOKUP_FIELD_CANDIDATES.superRegion,
              LOCATION_LOOKUP_FIELD_CANDIDATES.country,
              LOCATION_LOOKUP_FIELD_CANDIDATES.office,
            ]),
        )
      ) {
        return true;
      }
    }

    return false;
  }, [doesFieldMatchLocationCandidates, orderDetails]);

  useEffect(() => {
    let isCancelled = false;

    if (!hasLocationHierarchyFields) {
      setLocationHierarchyLookupResponse(null);
      return () => {
        isCancelled = true;
      };
    }

    const fetchLocationHierarchyLookupData = async () => {
      try {
        const response =
          await LookupService.getSupRegCountryOfficeDataByRegion();

        if (isCancelled) {
          return;
        }

        setLocationHierarchyLookupResponse(response || null);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error(
          "Failed to fetch super region, country and office lookup data:",
          error,
        );
        setLocationHierarchyLookupResponse(null);
      }
    };

    fetchLocationHierarchyLookupData();

    return () => {
      isCancelled = true;
    };
  }, [hasLocationHierarchyFields]);

  const locationHierarchyLookupData = useMemo(
    () => getLocationHierarchyRecords(locationHierarchyLookupResponse),
    [locationHierarchyLookupResponse],
  );

  const otherSectionConfigsBySection = useMemo(() => {
    const sectionsByName = new Map();

    for (const section of orderDetails || []) {
      if (
        section?.section === EFFORT_SECTION_NAME ||
        section?.section === SAVING_SECTION_NAME
      ) {
        sectionsByName.set(section.section, section);
      }
    }

    return sectionsByName;
  }, [orderDetails]);

  const baseOtherSectionRowsBySection = useMemo(() => {
    const rowsBySection = new Map();

    for (const section of orderDetails || []) {
      if (
        section?.section === EFFORT_SECTION_NAME ||
        section?.section === SAVING_SECTION_NAME
      ) {
        rowsBySection.set(
          section.section,
          createNormalizedSectionRows(section),
        );
      }
    }

    return rowsBySection;
  }, [orderDetails]);

  const draftOtherSectionRowsBySection = useMemo(() => {
    const rowsBySection = new Map();

    if (!isEditing) {
      return rowsBySection;
    }

    [EFFORT_SECTION_NAME, SAVING_SECTION_NAME].forEach((sectionName) => {
      const draftRows = draftOtherSectionRows[sectionName] || [];

      if (draftRows.length > 0) {
        rowsBySection.set(sectionName, draftRows);
      }
    });

    return rowsBySection;
  }, [draftOtherSectionRows, isEditing]);

  const displayOtherSectionRowsBySection = useMemo(() => {
    const rowsBySection = new Map(baseOtherSectionRowsBySection);

    [EFFORT_SECTION_NAME, SAVING_SECTION_NAME].forEach((sectionName) => {
      const baseRows = baseOtherSectionRowsBySection.get(sectionName) || [];
      const draftRows = draftOtherSectionRowsBySection.get(sectionName) || [];

      if (baseRows.length > 0 || draftRows.length > 0) {
        rowsBySection.set(
          sectionName,
          [...baseRows, ...draftRows].filter(
            (row) => !removedOtherSectionRowKeys.includes(row.rowKey),
          ),
        );
      }
    });

    return rowsBySection;
  }, [
    baseOtherSectionRowsBySection,
    draftOtherSectionRowsBySection,
    removedOtherSectionRowKeys,
  ]);

  const effortSectionRows = useMemo(
    () => displayOtherSectionRowsBySection.get(EFFORT_SECTION_NAME) || [],
    [displayOtherSectionRowsBySection],
  );

  const savingSectionRows = useMemo(
    () => displayOtherSectionRowsBySection.get(SAVING_SECTION_NAME) || [],
    [displayOtherSectionRowsBySection],
  );

  const sectionRowsByKey = useMemo(() => {
    const rowsByKey = new Map();

    [...effortSectionRows, ...savingSectionRows].forEach((row) => {
      rowsByKey.set(row.rowKey, row);
    });

    return rowsByKey;
  }, [effortSectionRows, savingSectionRows]);

  const findOrderFieldByCandidates = useCallback(
    (candidates = []) => {
      for (const section of orderDetails || []) {
        const pageFields = Array.isArray(section?.pagefields)
          ? section.pagefields
          : [];

        const matchingField = pageFields.find((field) =>
          doesFieldMatchCandidates(field, candidates),
        );

        if (matchingField) {
          return matchingField;
        }
      }

      return null;
    },
    [orderDetails],
  );

  const getOrderFieldCurrentValueByCandidates = useCallback(
    (candidates = []) => {
      const matchingField = findOrderFieldByCandidates(candidates);

      if (!matchingField) {
        return "";
      }

      const fieldStateKey = getFieldStateKey(matchingField);
      const editedField = fieldValues.find(
        (item) => item.fieldName === fieldStateKey,
      );

      if (editedField) {
        return editedField.updatedValue;
      }

      return matchingField.fieldValue || "";
    },
    [fieldValues, findOrderFieldByCandidates],
  );

  const getSavingsLookupPayload = useCallback(() => {
    const analyzerField = findOrderFieldByCandidates(
      Array.from(ANALYZER_TYPE_FIELD_NAMES),
    );
    const analyzerValue = getOrderFieldCurrentValueByCandidates(
      Array.from(ANALYZER_TYPE_FIELD_NAMES),
    );
    const analyzerOption = getDropdownFieldOptions(analyzerField).find(
      (option) => doesDropdownOptionMatchValue(option, analyzerValue),
    );
    const analyzerId = Number(
      analyzerOption?.id ??
        (analyzerOption
          ? getDropdownFieldOptionValue(analyzerOption)
          : analyzerValue),
    );

    const regionField = findOrderFieldByCandidates(
      APPLICATION_USED_LOOKUP_REGION_FIELD_CANDIDATES,
    );
    const regionValue = getOrderFieldCurrentValueByCandidates(
      APPLICATION_USED_LOOKUP_REGION_FIELD_CANDIDATES,
    );
    const regionOption = getDropdownFieldOptions(regionField).find((option) =>
      doesDropdownOptionMatchValue(option, regionValue),
    );
    const regionId = Number(
      regionOption?.id ??
        (regionOption ? getDropdownFieldOptionValue(regionOption) : regionValue),
    );

    return {
      analyzerId: Number.isFinite(analyzerId) ? analyzerId : 0,
      analyzerType: String(
        analyzerOption
          ? getDropdownFieldOptionLabel(analyzerOption)
          : headerData.analyzerType || headerData.analyzer || analyzerValue,
      ).trim(),
      regionId: Number.isFinite(regionId) ? regionId : 0,
    };
  }, [
    findOrderFieldByCandidates,
    getOrderFieldCurrentValueByCandidates,
    headerData.analyzer,
    headerData.analyzerType,
  ]);

  const wasOtherTabActiveRef = useRef(false);

  useEffect(() => {
    if (activeTab !== ORDER_TABS.OTHER) {
      wasOtherTabActiveRef.current = false;
      return;
    }

    if (wasOtherTabActiveRef.current) {
      return;
    }

    wasOtherTabActiveRef.current = true;
    setSavingsApplicationsResponse(null);

    LookupService.postSavingsList(getSavingsLookupPayload())
      .then((response) => {
        if (isMountedRef.current) {
          setSavingsApplicationsResponse(response || null);
        }
      })
      .catch((error) => {
        console.error("Failed to post savings lookup data:", error);
        if (isMountedRef.current) {
          setSavingsApplicationsResponse(null);
        }
      });
  }, [activeTab, getSavingsLookupPayload]);

  useEffect(() => {
    let isCancelled = false;

    if (!otherSectionConfigsBySection.has(SAVING_SECTION_NAME)) {
      setApplicationUsedHoursReasonLookupResponse(null);
      return () => {
        isCancelled = true;
      };
    }

    const fetchApplicationUsedHoursReasonLookup = async () => {
      try {
        const response =
          await LookupService.getApplicationUsedHoursReasonData();

        if (isCancelled || !isMountedRef.current) {
          return;
        }

        setApplicationUsedHoursReasonLookupResponse(response || null);
      } catch (error) {
        if (isCancelled || !isMountedRef.current) {
          return;
        }

        console.error(
          "Failed to fetch application used hours and reason lookup data:",
          error,
        );
        setApplicationUsedHoursReasonLookupResponse(null);
      }
    };

    fetchApplicationUsedHoursReasonLookup();

    return () => {
      isCancelled = true;
    };
  }, [orderDetails, otherSectionConfigsBySection]);

  const hasResolvedSavingsApplications = Array.isArray(
    (savingsApplicationsResponse?.data ?? savingsApplicationsResponse)
      ?.applications,
  );

  const applicationUsedHoursReasonLookupRegions = useMemo(
    () =>
      getApplicationUsedHoursReasonRegions(
        hasResolvedSavingsApplications
          ? savingsApplicationsResponse
          : applicationUsedHoursReasonLookupResponse,
      ),
    [
      applicationUsedHoursReasonLookupResponse,
      hasResolvedSavingsApplications,
      savingsApplicationsResponse,
    ],
  );

  const allApplicationUsedHoursReasonLookupEntries = useMemo(
    () =>
      buildApplicationUsedHoursReasonEntries(
        applicationUsedHoursReasonLookupRegions,
      ),
    [applicationUsedHoursReasonLookupRegions],
  );

  const savingsApplicationOptions = useMemo(() => {
    const optionsById = new Map();

    allApplicationUsedHoursReasonLookupEntries.forEach((application) => {
      if (
        application?.applicationUsedId == null ||
        !application?.applicationUsed
      ) {
        return;
      }

      const applicationId = String(application.applicationUsedId);
      optionsById.set(applicationId, {
        value: applicationId,
        field: application.applicationUsed,
        rawValue: applicationId,
        isDisabled: false,
      });
    });

    return Array.from(optionsById.values());
  }, [allApplicationUsedHoursReasonLookupEntries]);

  const applicationUsedHoursReasonLookupEntries = useMemo(() => {
    const selectedRegionValue = getOrderFieldCurrentValueByCandidates(
      APPLICATION_USED_LOOKUP_REGION_FIELD_CANDIDATES,
    );
    const normalizedSelectedRegionValue =
      normalizeLookupMatchValue(selectedRegionValue);

    if (!normalizedSelectedRegionValue) {
      return allApplicationUsedHoursReasonLookupEntries;
    }

    const matchedRegion =
      applicationUsedHoursReasonLookupRegions.find((region) =>
        getApplicationUsedHoursReasonRegionMatchValues(region).some(
          (candidateValue) => candidateValue === normalizedSelectedRegionValue,
        ),
      ) || null;

    if (!matchedRegion) {
      return allApplicationUsedHoursReasonLookupEntries;
    }

    return buildApplicationUsedHoursReasonEntries([matchedRegion]);
  }, [
    allApplicationUsedHoursReasonLookupEntries,
    applicationUsedHoursReasonLookupRegions,
    getOrderFieldCurrentValueByCandidates,
  ]);

  const findMatchedApplicationUsedHoursReasonEntry = useCallback(
    (applicationField, selectedValue) => {
      const normalizedSelectedValue = normalizeLookupMatchValue(selectedValue);

      if (!normalizedSelectedValue) {
        return null;
      }

      const matchCandidates = new Set([normalizedSelectedValue]);

      getDropdownFieldOptions(applicationField).forEach((option) => {
        if (!doesDropdownOptionMatchValue(option, selectedValue)) {
          return;
        }

        getDropdownFieldOptionMatchValues(option).forEach((optionValue) => {
          const normalizedOptionValue = normalizeLookupMatchValue(optionValue);

          if (normalizedOptionValue) {
            matchCandidates.add(normalizedOptionValue);
          }
        });
      });

      const findMatchingEntry = (entries = []) => {
        return (
          entries.find((applicationUsed) =>
            getApplicationUsedHoursReasonMatchValues(applicationUsed).some(
              (candidateValue) => matchCandidates.has(candidateValue),
            ),
          ) || null
        );
      };

      return (
        findMatchingEntry(applicationUsedHoursReasonLookupEntries) ||
        findMatchingEntry(allApplicationUsedHoursReasonLookupEntries)
      );
    },
    [
      allApplicationUsedHoursReasonLookupEntries,
      applicationUsedHoursReasonLookupEntries,
    ],
  );

  const getSavingReasonOptionsForRow = useCallback(
    (rowKey) => savingReasonOptionsByRow[rowKey] || [],
    [savingReasonOptionsByRow],
  );

  const findLocationFieldByCandidates = useCallback(
    (candidates = []) => {
      for (const section of orderDetails || []) {
        const pageFields = Array.isArray(section?.pagefields)
          ? section.pagefields
          : [];

        const matchingField = pageFields.find((field) =>
          doesFieldMatchLocationCandidates(field, candidates),
        );

        if (matchingField) {
          return matchingField;
        }
      }

      return null;
    },
    [doesFieldMatchLocationCandidates, orderDetails],
  );

  const getLocationFieldStateKeyByCandidates = useCallback(
    (candidates = []) =>
      getFieldStateKey(findLocationFieldByCandidates(candidates)),
    [findLocationFieldByCandidates],
  );

  const getLocationFieldCurrentValueByCandidates = useCallback(
    (candidates = []) => {
      const matchingField = findLocationFieldByCandidates(candidates);

      if (!matchingField) {
        return "";
      }

      const fieldStateKey = getFieldStateKey(matchingField);
      const editedField = fieldValues.find(
        (item) => item.fieldName === fieldStateKey,
      );

      if (editedField) {
        return editedField.updatedValue;
      }

      return matchingField.fieldValue || "";
    },
    [fieldValues, findLocationFieldByCandidates],
  );

  const getLocationLookupOptionsForField = useCallback(
    (field) => {
      const regions = locationHierarchyLookupData;

      if (
        doesFieldMatchLocationCandidates(
          field,
          LOCATION_LOOKUP_FIELD_CANDIDATES.area,
        )
      ) {
        return regions.map(buildRegionLookupOption).filter(Boolean);
      }

      const selectedAreaValue = getLocationFieldCurrentValueByCandidates(
        LOCATION_LOOKUP_FIELD_CANDIDATES.area,
      );
      const matchedRegion = findMatchedLocationRecord(
        regions,
        selectedAreaValue,
        buildRegionLookupOption,
      );

      if (
        doesFieldMatchCandidates(
          field,
          LOCATION_LOOKUP_FIELD_CANDIDATES.superRegion,
        )
      ) {
        return (matchedRegion?.superRegions || [])
          .map(buildSuperRegionLookupOption)
          .filter(Boolean);
      }

      const selectedSuperRegionValue = getLocationFieldCurrentValueByCandidates(
        LOCATION_LOOKUP_FIELD_CANDIDATES.superRegion,
      );
      const matchedSuperRegion = findMatchedLocationRecord(
        matchedRegion?.superRegions || [],
        selectedSuperRegionValue,
        buildSuperRegionLookupOption,
      );

      if (
        doesFieldMatchCandidates(
          field,
          LOCATION_LOOKUP_FIELD_CANDIDATES.country,
        )
      ) {
        return (matchedSuperRegion?.countries || [])
          .map(buildCountryLookupOption)
          .filter(Boolean);
      }

      const selectedCountryValue = getLocationFieldCurrentValueByCandidates(
        LOCATION_LOOKUP_FIELD_CANDIDATES.country,
      );
      const matchedCountry = findMatchedLocationRecord(
        matchedSuperRegion?.countries || [],
        selectedCountryValue,
        buildCountryLookupOption,
      );

      if (
        doesFieldMatchCandidates(field, LOCATION_LOOKUP_FIELD_CANDIDATES.office)
      ) {
        return (matchedCountry?.officelocations || [])
          .map(buildOfficeLookupOption)
          .filter(Boolean);
      }

      return null;
    },
    [
      doesFieldMatchLocationCandidates,
      getLocationFieldCurrentValueByCandidates,
      locationHierarchyLookupData,
    ],
  );

  const isFilesTabRestrictedCountry = useMemo(() => {
    const restrictedCountries = new Set([
      "germany",
      "switzerland",
      "austria",
      "saudi arabia",
    ]);
    const countryField = findLocationFieldByCandidates(
      LOCATION_LOOKUP_FIELD_CANDIDATES.country,
    );
    const countryValue = getLocationFieldCurrentValueByCandidates(
      LOCATION_LOOKUP_FIELD_CANDIDATES.country,
    );
    const normalizedCountryValue = normalizeLocationLookupValue(countryValue)
      .toLowerCase();
    if (restrictedCountries.has(normalizedCountryValue)) return true;

    const matchingFieldOption = (countryField?.fieldValueList || []).find(
      (option) => doesDropdownOptionMatchValue(option, countryValue),
    );
    const optionLabel = normalizeLocationLookupValue(
      getDropdownFieldOptionLabel(matchingFieldOption),
    ).toLowerCase();
    if (restrictedCountries.has(optionLabel)) return true;

    const allCountries = locationHierarchyLookupData.flatMap((region) =>
      (region?.superRegions || []).flatMap((superRegion) =>
        superRegion?.countries || [],
      ),
    );
    const matchedCountry = findMatchedLocationRecord(
      allCountries,
      countryValue,
      buildCountryLookupOption,
    );
    const resolvedCountryName = normalizeLocationLookupValue(
      matchedCountry?.countryName,
    ).toLowerCase();
    if (restrictedCountries.has(resolvedCountryName)) return true;

    return [headerData.country, headerData.countryName, headerData.country_name]
      .some((value) => restrictedCountries.has(
        normalizeLocationLookupValue(value).toLowerCase(),
      ));
  }, [
    findLocationFieldByCandidates,
    getLocationFieldCurrentValueByCandidates,
    headerData,
    locationHierarchyLookupData,
  ]);

  const isFilesTabAvailable =
    isGeneralLedgerAnalyzer && !isFilesTabRestrictedCountry;

  const getResolvedFieldDefinition = useCallback(
    (field) => {
      if (
        field?.sectionName === SAVING_SECTION_NAME &&
        doesFieldMatchCandidates(
          field,
          SAVING_PAYLOAD_FIELD_CANDIDATES.applicationUsedId,
        )
      ) {
        const applicationUsedOptions = hasResolvedSavingsApplications
          ? savingsApplicationOptions
          : savingsApplicationOptions.length > 0
            ? savingsApplicationOptions
            : getKeyBackedDropdownOptions(field?.dropdownApplicationUsed);

        if (hasResolvedSavingsApplications || applicationUsedOptions.length > 0) {
          return {
            ...field,
            dataType: "dropdown",
            fieldValueList: applicationUsedOptions,
          };
        }
      }

      if (
        field?.sectionName === SAVING_SECTION_NAME &&
        doesFieldMatchCandidates(
          field,
          SAVING_PAYLOAD_FIELD_CANDIDATES.reasonId,
        )
      ) {
        const rowReasonOptions = getKeyBackedDropdownOptions(
          field?.dropdownReason,
        );
        const savingReasonOptions = getSavingReasonOptionsForRow(field.rowKey);

        return {
          ...field,
          dataType: "dropdown",
          fieldValueList: hasResolvedSavingsApplications
            ? savingReasonOptions
            : savingReasonOptions.length > 0
              ? savingReasonOptions
              : rowReasonOptions,
        };
      }

      if (
        field?.sectionName === EFFORT_SECTION_NAME &&
        doesFieldMatchCandidates(field, EFFORT_ROW_FIELD_CANDIDATES.role)
      ) {
        const roleOptions = getKeyBackedDropdownOptions(field?.dropdownRoleFor);

        if (roleOptions.length > 0) {
          return {
            ...field,
            dataType: "dropdown",
            fieldValueList: roleOptions,
          };
        }
      }

      if (
        field?.sectionName === EFFORT_SECTION_NAME &&
        doesFieldMatchCandidates(
          field,
          EFFORT_PAYLOAD_FIELD_CANDIDATES.effortForMasterId,
        )
      ) {
        const effortForOptions = getKeyBackedDropdownOptions(
          field?.dropdownEffortFor,
        );

        if (effortForOptions.length > 0) {
          return {
            ...field,
            dataType: "dropdown",
            fieldValueList: effortForOptions,
          };
        }
      }

      const locationLookupOptions = getLocationLookupOptionsForField(field);

      if (locationLookupOptions !== null) {
        return {
          ...field,
          dataType: "dropdown",
          fieldValueList: locationLookupOptions,
        };
      }

      return field;
    },

    [
      getLocationLookupOptionsForField,
      getSavingReasonOptionsForRow,
      hasResolvedSavingsApplications,
      savingsApplicationOptions,
    ],
  );

  const normalizedOrderFields = useMemo(() => {
    const fields = [];

    for (const section of orderDetails || []) {
      if (
        section?.section === EFFORT_SECTION_NAME ||
        section?.section === SAVING_SECTION_NAME
      ) {
        const sectionRows =
          baseOtherSectionRowsBySection.get(section.section) || [];
        const draftSectionRows =
          draftOtherSectionRowsBySection.get(section.section) || [];

        sectionRows.forEach((row) => {
          row.fields.forEach((field) => {
            fields.push(getResolvedFieldDefinition(field));
          });
        });

        draftSectionRows.forEach((row) => {
          row.fields.forEach((field) => {
            fields.push(getResolvedFieldDefinition(field));
          });
        });

        continue;
      }

      for (const field of section.pagefields || []) {
        fields.push(
          getResolvedFieldDefinition({
            ...field,
            sectionName: section.section || "",
            rowId: null,
            rowIndex: 0,
            rowKey: "",
            baseFieldName: String(field?.field || ""),
            fieldStateKey: String(field?.field || ""),
          }),
        );
      }
    }

    return fields;
  }, [
    orderDetails,
    baseOtherSectionRowsBySection,
    draftOtherSectionRowsBySection,
    getResolvedFieldDefinition,
  ]);

  const fieldDefinitionsMap = useMemo(() => {
    const definitions = new Map();

    normalizedOrderFields.forEach((field) => {
      const fieldKey = getFieldStateKey(field);

      if (fieldKey) {
        definitions.set(fieldKey, field);
      }
    });

    return definitions;
  }, [normalizedOrderFields]);

  const fieldSectionMap = useMemo(() => {
    const sections = new Map();

    normalizedOrderFields.forEach((field) => {
      const fieldKey = getFieldStateKey(field);

      if (fieldKey) {
        sections.set(fieldKey, field.sectionName || "");
      }
    });

    return sections;
  }, [normalizedOrderFields]);

  const fieldTabLabelMap = useMemo(() => {
    const tabLabels = new Map();

    normalizedOrderFields.forEach((field) => {
      const tabLabel = SECTION_TO_TAB_LABEL[field.sectionName];

      if (!tabLabel) {
        return;
      }

      const fieldKey = getFieldStateKey(field);

      if (fieldKey) {
        tabLabels.set(fieldKey, tabLabel);
      }
    });

    return tabLabels;
  }, [normalizedOrderFields]);

  const getCurrentFieldValue = useCallback(
    (fieldKey) => {
      const editedField = fieldValues.find(
        (item) => item.fieldName === fieldKey,
      );

      if (editedField) {
        return editedField.updatedValue;
      }

      return fieldDefinitionsMap.get(fieldKey)?.fieldValue || "";
    },
    [fieldDefinitionsMap, fieldValues],
  );

  const getFieldValueFromSnapshot = useCallback(
    (fieldKey, updatedFieldValues) => {
      const editedField = updatedFieldValues.find(
        (item) => item.fieldName === fieldKey,
      );

      if (editedField) {
        return editedField.updatedValue;
      }

      return fieldDefinitionsMap.get(fieldKey)?.fieldValue || "";
    },
    [fieldDefinitionsMap],
  );

  const upsertFieldValue = useCallback((values, fieldKey, updatedValue) => {
    if (!fieldKey) {
      return values;
    }

    const existingIndex = values.findIndex(
      (item) => item.fieldName === fieldKey,
    );

    if (existingIndex >= 0) {
      const updatedValues = [...values];
      updatedValues[existingIndex] = {
        fieldName: fieldKey,
        updatedValue,
      };
      return updatedValues;
    }

    return [
      ...values,
      {
        fieldName: fieldKey,
        updatedValue,
      },
    ];
  }, []);

  const getSectionRowField = useCallback(
    (row, candidates = []) => findMatchingRowField(row, candidates),
    [],
  );

  const getSectionRowFieldKey = useCallback(
    (row, candidates = []) =>
      getFieldStateKey(getSectionRowField(row, candidates)),
    [getSectionRowField],
  );

  const resolveFieldOptionValue = useCallback((field, value) => {
    const normalizedValue = String(value || "").trim();

    if (!field || !normalizedValue) {
      return "";
    }

    const fieldOptions = Array.isArray(field.fieldValueList)
      ? field.fieldValueList
      : [];

    const matchingOption = fieldOptions.find((option) =>
      doesDropdownOptionMatchValue(option, normalizedValue),
    );

    return matchingOption?.value || normalizedValue;
  }, []);

  const getEffortContributorFieldKey = useCallback(
    (roleFieldKey, selectedRole) => {
      if (!roleFieldKey) {
        return "";
      }

      const effortRoleFieldDefinition = fieldDefinitionsMap.get(roleFieldKey);
      const resolvedRoleValue = resolveFieldOptionValue(
        effortRoleFieldDefinition,
        selectedRole,
      );
      const matchingRoleOption = getDropdownFieldOptions(
        effortRoleFieldDefinition,
      ).find((option) =>
        doesDropdownOptionMatchValue(
          option,
          resolvedRoleValue || selectedRole || "",
        ),
      );
      const resolvedRoleLabel = matchingRoleOption
        ? getDropdownFieldOptionLabel(matchingRoleOption)
        : selectedRole;
      const normalizedRole = String(resolvedRoleLabel || selectedRole || "")
        .trim()
        .toLowerCase();

      return CONTRIBUTOR_ROLE_FIELD_MAP[normalizedRole] || "";
    },
    [fieldDefinitionsMap, resolveFieldOptionValue],
  );

  const shouldShowSavingOtherReasonForRow = useCallback(
    (row) => {
      const otherReasonField = getSectionRowField(
        row,
        SAVING_ROW_FIELD_CANDIDATES.otherReason,
      );
      const reasonField = getSectionRowField(
        row,
        SAVING_PAYLOAD_FIELD_CANDIDATES.reasonId,
      );

      if (!otherReasonField || !reasonField) {
        return false;
      }

      const currentReasonValue = String(
        getCurrentFieldValue(getFieldStateKey(reasonField)) || "",
      ).trim();

      if (!currentReasonValue) {
        return false;
      }

      const matchingReasonOption = getSavingReasonOptionsForRow(
        row.rowKey,
      ).find((option) =>
        doesDropdownOptionMatchValue(option, currentReasonValue),
      );

      const selectedReasonLabel = matchingReasonOption
        ? getDropdownFieldOptionLabel(matchingReasonOption)
        : currentReasonValue;

      return (
        String(selectedReasonLabel || "")
          .trim()
          .toLowerCase() === "others"
      );
    },
    [getCurrentFieldValue, getSavingReasonOptionsForRow, getSectionRowField],
  );

  const shouldShowSavingOtherReasonField = useCallback(
    (field) => {
      if (
        !isSavingOtherReasonField(field) ||
        field?.sectionName !== SAVING_SECTION_NAME
      ) {
        return true;
      }

      const row = sectionRowsByKey.get(field.rowKey);

      return row ? shouldShowSavingOtherReasonForRow(row) : false;
    },
    [sectionRowsByKey, shouldShowSavingOtherReasonForRow],
  );

  const shouldShowOnHoldOtherReasonField = useCallback(
    (field) => {
      if (!isOtherOnHoldReasonField(field)) {
        return true;
      }

      const fieldStateKey = getFieldStateKey(field);
      const rowKey = field?.rowKey || "";
      const sectionName = field?.sectionName || "";
      const controllingField = Array.from(fieldDefinitionsMap.values()).find(
        (candidateField) => {
          if (getFieldStateKey(candidateField) === fieldStateKey) {
            return false;
          }

          if (
            rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== rowKey
          ) {
            return false;
          }

          if (
            sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== sectionName
          ) {
            return false;
          }

          return isOnHoldReasonField(candidateField);
        },
      );

      if (!controllingField) {
        return true;
      }

      const currentReasonValue = String(
        getCurrentFieldValue(getFieldStateKey(controllingField)) || "",
      ).trim();

      if (!currentReasonValue) {
        return false;
      }

      const resolvedReasonValue =
        resolveFieldOptionValue(controllingField, currentReasonValue) ||
        currentReasonValue;
      const matchingReasonOption = getDropdownFieldOptions(
        controllingField,
      ).find((option) =>
        doesDropdownOptionMatchValue(option, resolvedReasonValue),
      );
      const selectedReasonLabel = matchingReasonOption
        ? getDropdownFieldOptionLabel(matchingReasonOption)
        : currentReasonValue;

      return (
        isOtherLikeOptionValue(selectedReasonLabel) ||
        isOtherLikeOptionValue(resolvedReasonValue)
      );
    },
    [fieldDefinitionsMap, getCurrentFieldValue, resolveFieldOptionValue],
  );

  const shouldShowOtherCleansingToolField = useCallback(
    (field) => {
      if (!isOtherCleansingToolField(field)) {
        return true;
      }

      const fieldStateKey = getFieldStateKey(field);
      const rowKey = field?.rowKey || "";
      const sectionName = field?.sectionName || "";
      const controllingField = Array.from(fieldDefinitionsMap.values()).find(
        (candidateField) => {
          if (getFieldStateKey(candidateField) === fieldStateKey) {
            return false;
          }

          if (
            rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== rowKey
          ) {
            return false;
          }

          if (
            sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== sectionName
          ) {
            return false;
          }

          return isCleansingToolField(candidateField);
        },
      );

      if (!controllingField) {
        return true;
      }

      const currentCleansingToolValue = String(
        getCurrentFieldValue(getFieldStateKey(controllingField)) || "",
      ).trim();

      if (!currentCleansingToolValue) {
        return false;
      }

      const resolvedCleansingToolValue =
        resolveFieldOptionValue(controllingField, currentCleansingToolValue) ||
        currentCleansingToolValue;
      const matchingCleansingToolOption = getDropdownFieldOptions(
        controllingField,
      ).find((option) =>
        doesDropdownOptionMatchValue(option, resolvedCleansingToolValue),
      );
      const selectedCleansingToolLabel = matchingCleansingToolOption
        ? getDropdownFieldOptionLabel(matchingCleansingToolOption)
        : currentCleansingToolValue;

      return (
        isOtherLikeOptionValue(selectedCleansingToolLabel) ||
        isOtherLikeOptionValue(resolvedCleansingToolValue)
      );
    },
    [fieldDefinitionsMap, getCurrentFieldValue, resolveFieldOptionValue],
  );

  const shouldShowOtherAnalyzerTypeField = useCallback(
    (field) => {
      if (!isOtherAnalyzerTypeField(field)) {
        return true;
      }

      const fieldStateKey = getFieldStateKey(field);
      const rowKey = field?.rowKey || "";
      const sectionName = field?.sectionName || "";
      const controllingField = Array.from(fieldDefinitionsMap.values()).find(
        (candidateField) => {
          if (getFieldStateKey(candidateField) === fieldStateKey) {
            return false;
          }

          if (
            rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== rowKey
          ) {
            return false;
          }

          if (
            sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== sectionName
          ) {
            return false;
          }

          return isAnalyzerTypeField(candidateField);
        },
      );

      if (!controllingField) {
        return true;
      }

      const currentAnalyzerTypeValue = String(
        getCurrentFieldValue(getFieldStateKey(controllingField)) || "",
      ).trim();

      if (!currentAnalyzerTypeValue) {
        return false;
      }

      const resolvedAnalyzerTypeValue =
        resolveFieldOptionValue(controllingField, currentAnalyzerTypeValue) ||
        currentAnalyzerTypeValue;
      const matchingAnalyzerTypeOption = getDropdownFieldOptions(
        controllingField,
      ).find((option) =>
        doesDropdownOptionMatchValue(option, resolvedAnalyzerTypeValue),
      );
      const selectedAnalyzerTypeLabel = matchingAnalyzerTypeOption
        ? getDropdownFieldOptionLabel(matchingAnalyzerTypeOption)
        : currentAnalyzerTypeValue;

      return (
        isOtherLikeOptionValue(selectedAnalyzerTypeLabel) ||
        isOtherLikeOptionValue(resolvedAnalyzerTypeValue)
      );
    },
    [fieldDefinitionsMap, getCurrentFieldValue, resolveFieldOptionValue],
  );

  const shouldShowOtherMarketSegmentField = useCallback(
    (field) => {
      if (!isOtherMarketSegmentField(field)) {
        return true;
      }

      const fieldStateKey = getFieldStateKey(field);
      const rowKey = field?.rowKey || "";
      const sectionName = field?.sectionName || "";
      const controllingField = Array.from(fieldDefinitionsMap.values()).find(
        (candidateField) => {
          if (getFieldStateKey(candidateField) === fieldStateKey) {
            return false;
          }

          if (
            rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== rowKey
          ) {
            return false;
          }

          if (
            sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== sectionName
          ) {
            return false;
          }

          return isMarketSegmentField(candidateField);
        },
      );

      if (!controllingField) {
        return true;
      }

      const currentMarketSegmentValue = String(
        getCurrentFieldValue(getFieldStateKey(controllingField)) || "",
      ).trim();

      if (!currentMarketSegmentValue) {
        return false;
      }

      const resolvedMarketSegmentValue =
        resolveFieldOptionValue(controllingField, currentMarketSegmentValue) ||
        currentMarketSegmentValue;
      const matchingMarketSegmentOption = getDropdownFieldOptions(
        controllingField,
      ).find((option) =>
        doesDropdownOptionMatchValue(option, resolvedMarketSegmentValue),
      );
      const selectedMarketSegmentLabel = matchingMarketSegmentOption
        ? getDropdownFieldOptionLabel(matchingMarketSegmentOption)
        : currentMarketSegmentValue;

      return (
        isOtherLikeOptionValue(selectedMarketSegmentLabel) ||
        isOtherLikeOptionValue(resolvedMarketSegmentValue)
      );
    },
    [fieldDefinitionsMap, getCurrentFieldValue, resolveFieldOptionValue],
  );

  const shouldShowToolIssueDescriptionField = useCallback(
    (field) => {
      if (!isToolIssueDescriptionField(field)) {
        return true;
      }

      const fieldStateKey = getFieldStateKey(field);
      const rowKey = field?.rowKey || "";
      const sectionName = field?.sectionName || "";
      const controllingField = Array.from(fieldDefinitionsMap.values()).find(
        (candidateField) => {
          if (getFieldStateKey(candidateField) === fieldStateKey) {
            return false;
          }

          if (
            rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== rowKey
          ) {
            return false;
          }

          if (
            sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== sectionName
          ) {
            return false;
          }

          return isToolIssueTypeField(candidateField);
        },
      );

      if (!controllingField) {
        return true;
      }

      const currentToolIssueTypeValue = String(
        getCurrentFieldValue(getFieldStateKey(controllingField)) || "",
      ).trim();

      if (!currentToolIssueTypeValue) {
        return false;
      }

      const resolvedToolIssueTypeValue =
        resolveFieldOptionValue(controllingField, currentToolIssueTypeValue) ||
        currentToolIssueTypeValue;
      const matchingToolIssueTypeOption = getDropdownFieldOptions(
        controllingField,
      ).find((option) =>
        doesDropdownOptionMatchValue(option, resolvedToolIssueTypeValue),
      );
      const selectedToolIssueTypeLabel = matchingToolIssueTypeOption
        ? getDropdownFieldOptionLabel(matchingToolIssueTypeOption)
        : currentToolIssueTypeValue;

      return (
        isToolIssueDescriptionTriggerValue(selectedToolIssueTypeLabel) ||
        isToolIssueDescriptionTriggerValue(resolvedToolIssueTypeValue)
      );
    },
    [fieldDefinitionsMap, getCurrentFieldValue, resolveFieldOptionValue],
  );

  const shouldShowReprocessDescriptionField = useCallback(
    (field) => {
      if (!isReprocessDescriptionField(field)) {
        return true;
      }

      const fieldStateKey = getFieldStateKey(field);
      const rowKey = field?.rowKey || "";
      const sectionName = field?.sectionName || "";
      const controllingField = Array.from(fieldDefinitionsMap.values()).find(
        (candidateField) => {
          if (getFieldStateKey(candidateField) === fieldStateKey) {
            return false;
          }

          if (
            rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== rowKey
          ) {
            return false;
          }

          if (
            sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== sectionName
          ) {
            return false;
          }

          return isReprocessReasonField(candidateField);
        },
      );

      if (!controllingField) {
        return true;
      }

      const currentReprocessReasonValue = String(
        getCurrentFieldValue(getFieldStateKey(controllingField)) || "",
      ).trim();

      if (!currentReprocessReasonValue) {
        return false;
      }

      const resolvedReprocessReasonValue =
        resolveFieldOptionValue(
          controllingField,
          currentReprocessReasonValue,
        ) || currentReprocessReasonValue;
      const matchingReprocessReasonOption = getDropdownFieldOptions(
        controllingField,
      ).find((option) =>
        doesDropdownOptionMatchValue(option, resolvedReprocessReasonValue),
      );
      const selectedReprocessReasonLabel = matchingReprocessReasonOption
        ? getDropdownFieldOptionLabel(matchingReprocessReasonOption)
        : currentReprocessReasonValue;

      return (
        isReprocessDescriptionTriggerValue(selectedReprocessReasonLabel) ||
        isReprocessDescriptionTriggerValue(resolvedReprocessReasonValue)
      );
    },
    [fieldDefinitionsMap, getCurrentFieldValue, resolveFieldOptionValue],
  );

  const shouldShowOtherErpField = useCallback(
    (field) => {
      if (!isOtherErpField(field)) {
        return true;
      }

      const fieldStateKey = getFieldStateKey(field);
      const rowKey = field?.rowKey || "";
      const sectionName = field?.sectionName || "";
      const controllingField = Array.from(fieldDefinitionsMap.values()).find(
        (candidateField) => {
          if (getFieldStateKey(candidateField) === fieldStateKey) {
            return false;
          }

          if (
            rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== rowKey
          ) {
            return false;
          }

          if (
            sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== sectionName
          ) {
            return false;
          }

          return isErpNameField(candidateField);
        },
      );

      if (!controllingField) {
        return true;
      }

      const currentErpValue = String(
        getCurrentFieldValue(getFieldStateKey(controllingField)) || "",
      ).trim();

      if (!currentErpValue) {
        return false;
      }

      const resolvedErpValue =
        resolveFieldOptionValue(controllingField, currentErpValue) ||
        currentErpValue;
      const matchingErpOption = getDropdownFieldOptions(controllingField).find(
        (option) => doesDropdownOptionMatchValue(option, resolvedErpValue),
      );
      const selectedErpLabel = matchingErpOption
        ? getDropdownFieldOptionLabel(matchingErpOption)
        : currentErpValue;

      return (
        isOtherErpTriggerValue(selectedErpLabel) ||
        isOtherErpTriggerValue(resolvedErpValue)
      );
    },
    [fieldDefinitionsMap, getCurrentFieldValue, resolveFieldOptionValue],
  );

  const shouldShowOtherExtractionToolField = useCallback(
    (field) => {
      if (!isOtherExtractionToolField(field)) {
        return true;
      }

      const fieldStateKey = getFieldStateKey(field);
      const rowKey = field?.rowKey || "";
      const sectionName = field?.sectionName || "";
      const controllingField = Array.from(fieldDefinitionsMap.values()).find(
        (candidateField) => {
          if (getFieldStateKey(candidateField) === fieldStateKey) {
            return false;
          }

          if (
            rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== rowKey
          ) {
            return false;
          }

          if (
            sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== sectionName
          ) {
            return false;
          }

          return isExtractionToolField(candidateField);
        },
      );

      if (!controllingField) {
        return true;
      }

      const currentExtractionToolValue = String(
        getCurrentFieldValue(getFieldStateKey(controllingField)) || "",
      ).trim();

      if (!currentExtractionToolValue) {
        return false;
      }

      const resolvedExtractionToolValue =
        resolveFieldOptionValue(controllingField, currentExtractionToolValue) ||
        currentExtractionToolValue;
      const matchingExtractionToolOption = getDropdownFieldOptions(
        controllingField,
      ).find((option) =>
        doesDropdownOptionMatchValue(option, resolvedExtractionToolValue),
      );
      const selectedExtractionToolLabel = matchingExtractionToolOption
        ? getDropdownFieldOptionLabel(matchingExtractionToolOption)
        : currentExtractionToolValue;

      return (
        isOtherExtractionToolTriggerValue(selectedExtractionToolLabel) ||
        isOtherExtractionToolTriggerValue(resolvedExtractionToolValue)
      );
    },
    [fieldDefinitionsMap, getCurrentFieldValue, resolveFieldOptionValue],
  );

  const shouldShowCancellationOtherReasonField = useCallback(
    (field) => {
      if (!isOtherCancellationReasonField(field)) {
        return true;
      }

      const fieldStateKey = getFieldStateKey(field);
      const rowKey = field?.rowKey || "";
      const sectionName = field?.sectionName || "";
      const controllingField = Array.from(fieldDefinitionsMap.values()).find(
        (candidateField) => {
          if (getFieldStateKey(candidateField) === fieldStateKey) {
            return false;
          }

          if (
            rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== rowKey
          ) {
            return false;
          }

          if (
            sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== sectionName
          ) {
            return false;
          }

          return isCancellationReasonField(candidateField);
        },
      );

      if (!controllingField) {
        return true;
      }

      const currentReasonValue = String(
        getCurrentFieldValue(getFieldStateKey(controllingField)) || "",
      ).trim();

      if (!currentReasonValue) {
        return false;
      }

      const resolvedReasonValue =
        resolveFieldOptionValue(controllingField, currentReasonValue) ||
        currentReasonValue;
      const matchingReasonOption = getDropdownFieldOptions(
        controllingField,
      ).find((option) =>
        doesDropdownOptionMatchValue(option, resolvedReasonValue),
      );
      const selectedReasonLabel = matchingReasonOption
        ? getDropdownFieldOptionLabel(matchingReasonOption)
        : currentReasonValue;

      return (
        isOtherLikeOptionValue(selectedReasonLabel) ||
        isOtherLikeOptionValue(resolvedReasonValue)
      );
    },
    [fieldDefinitionsMap, getCurrentFieldValue, resolveFieldOptionValue],
  );

  const shouldIncludeRegularFieldInPayload = useCallback(
    (fieldDefinition) => {
      if (!fieldDefinition) {
        return true;
      }

      if (
        isOtherOnHoldReasonField(fieldDefinition) &&
        !shouldShowOnHoldOtherReasonField(fieldDefinition)
      ) {
        return false;
      }

      if (
        isOtherCancellationReasonField(fieldDefinition) &&
        !shouldShowCancellationOtherReasonField(fieldDefinition)
      ) {
        return false;
      }

      if (
        isOtherCleansingToolField(fieldDefinition) &&
        !shouldShowOtherCleansingToolField(fieldDefinition)
      ) {
        return false;
      }

      if (
        isOtherAnalyzerTypeField(fieldDefinition) &&
        !shouldShowOtherAnalyzerTypeField(fieldDefinition)
      ) {
        return false;
      }

      if (
        isOtherMarketSegmentField(fieldDefinition) &&
        !shouldShowOtherMarketSegmentField(fieldDefinition)
      ) {
        return false;
      }

      if (
        isOtherErpField(fieldDefinition) &&
        !shouldShowOtherErpField(fieldDefinition)
      ) {
        return false;
      }

      return true;
    },
    [
      shouldShowCancellationOtherReasonField,
      shouldShowOtherAnalyzerTypeField,
      shouldShowOtherMarketSegmentField,
      shouldShowOtherErpField,
      shouldShowOtherCleansingToolField,
      shouldShowOnHoldOtherReasonField,
    ],
  );

  useEffect(() => {
    if (!userEmail || savingSectionRows.length === 0) {
      return;
    }

    setFieldValues((prev) => {
      let updatedValues = prev;
      let hasChanges = false;

      savingSectionRows.forEach((row) => {
        const savingUserFieldKey = getSectionRowFieldKey(
          row,
          SAVING_ROW_FIELD_CANDIDATES.userName,
        );

        if (!savingUserFieldKey) {
          return;
        }

        const hasEditedUserName = updatedValues.some(
          (item) => item.fieldName === savingUserFieldKey,
        );

        if (hasEditedUserName) {
          return;
        }

        const originalUserNameValue =
          fieldDefinitionsMap.get(savingUserFieldKey)?.fieldValue;

        if (!isEmptyFieldValue(originalUserNameValue)) {
          return;
        }

        const currentUserNameValue = getFieldValueFromSnapshot(
          savingUserFieldKey,
          updatedValues,
        );

        if (currentUserNameValue === userEmail) {
          return;
        }

        updatedValues = upsertFieldValue(
          updatedValues,
          savingUserFieldKey,
          userEmail,
        );
        hasChanges = true;
      });

      return hasChanges ? updatedValues : prev;
    });
  }, [
    fieldDefinitionsMap,
    getFieldValueFromSnapshot,
    getSectionRowFieldKey,
    savingSectionRows,
    upsertFieldValue,
    userEmail,
  ]);

  useEffect(() => {
    if (savingSectionRows.length === 0) {
      setSavingReasonOptionsByRow((prev) => {
        return Object.keys(prev).length === 0 ? prev : {};
      });
      return;
    }

    const nextOptionsByRow = {};

    savingSectionRows.forEach((row) => {
      const applicationField = getSectionRowField(
        row,
        SAVING_PAYLOAD_FIELD_CANDIDATES.applicationUsedId,
      );
      const selectedApplicationValue = applicationField
        ? getCurrentFieldValue(getFieldStateKey(applicationField))
        : "";
      const matchedApplicationLookup =
        findMatchedApplicationUsedHoursReasonEntry(
          applicationField,
          selectedApplicationValue,
        );

      nextOptionsByRow[row.rowKey] = buildSavingReasonOptionsFromLookup(
        matchedApplicationLookup,
      );
    });

    setSavingReasonOptionsByRow((prev) =>
      JSON.stringify(prev) === JSON.stringify(nextOptionsByRow)
        ? prev
        : nextOptionsByRow,
    );
  }, [
    findMatchedApplicationUsedHoursReasonEntry,
    getCurrentFieldValue,
    getSectionRowField,
    savingSectionRows,
  ]);

  useEffect(() => {
    if (savingSectionRows.length === 0) {
      return;
    }

    setFieldValues((prev) => {
      let updatedValues = prev;
      let hasChanges = false;

      savingSectionRows.forEach((row) => {
        const applicationField = getSectionRowField(
          row,
          SAVING_PAYLOAD_FIELD_CANDIDATES.applicationUsedId,
        );
        const hoursFieldKey = getSectionRowFieldKey(
          row,
          SAVING_PAYLOAD_FIELD_CANDIDATES.hoursToSave,
        );

        if (!applicationField || !hoursFieldKey) {
          return;
        }

        const hasEditedHours = updatedValues.some(
          (item) => item.fieldName === hoursFieldKey,
        );

        if (hasEditedHours) {
          return;
        }

        const currentHoursValue = getFieldValueFromSnapshot(
          hoursFieldKey,
          updatedValues,
        );

        if (!shouldApplySavingDefaultHours(currentHoursValue)) {
          return;
        }

        const selectedApplicationValue = getFieldValueFromSnapshot(
          getFieldStateKey(applicationField),
          updatedValues,
        );
        const matchedApplicationLookup =
          findMatchedApplicationUsedHoursReasonEntry(
            applicationField,
            selectedApplicationValue,
          );
        const defaultHoursValue = formatSavingDefaultHoursValue(
          matchedApplicationLookup?.defaultHours,
        );

        if (
          !defaultHoursValue ||
          defaultHoursValue === String(currentHoursValue ?? "").trim()
        ) {
          return;
        }

        updatedValues = upsertFieldValue(
          updatedValues,
          hoursFieldKey,
          defaultHoursValue,
        );
        hasChanges = true;
      });

      return hasChanges ? updatedValues : prev;
    });
  }, [
    fieldValues,
    findMatchedApplicationUsedHoursReasonEntry,
    getFieldValueFromSnapshot,
    getSectionRowField,
    getSectionRowFieldKey,
    savingSectionRows,
    upsertFieldValue,
  ]);

  useEffect(() => {
    if (effortSectionRows.length === 0) {
      return;
    }

    setFieldValues((prev) => {
      let updatedValues = prev;
      let hasChanges = false;

      effortSectionRows.forEach((row) => {
        const roleFieldKey = getSectionRowFieldKey(
          row,
          EFFORT_ROW_FIELD_CANDIDATES.role,
        );
        const resourceNameFieldKey = getSectionRowFieldKey(
          row,
          EFFORT_ROW_FIELD_CANDIDATES.resourceName,
        );

        if (!roleFieldKey || !resourceNameFieldKey) {
          return;
        }

        const hasEditedResourceName = updatedValues.some(
          (item) => item.fieldName === resourceNameFieldKey,
        );

        if (hasEditedResourceName) {
          return;
        }

        const originalResourceNameValue =
          fieldDefinitionsMap.get(resourceNameFieldKey)?.fieldValue;

        if (!isEmptyFieldValue(originalResourceNameValue)) {
          return;
        }

        const selectedEffortRole = getFieldValueFromSnapshot(
          roleFieldKey,
          updatedValues,
        );
        const contributorFieldKey = getEffortContributorFieldKey(
          roleFieldKey,
          selectedEffortRole,
        );

        if (!contributorFieldKey) {
          return;
        }

        const contributorValue = getFieldValueFromSnapshot(
          contributorFieldKey,
          updatedValues,
        );

        if (isEmptyFieldValue(contributorValue)) {
          return;
        }

        const currentResourceName = getFieldValueFromSnapshot(
          resourceNameFieldKey,
          updatedValues,
        );

        if (currentResourceName === contributorValue) {
          return;
        }

        updatedValues = upsertFieldValue(
          updatedValues,
          resourceNameFieldKey,
          contributorValue,
        );
        hasChanges = true;
      });

      return hasChanges ? updatedValues : prev;
    });
  }, [
    effortSectionRows,
    fieldDefinitionsMap,
    getEffortContributorFieldKey,
    getFieldValueFromSnapshot,
    getSectionRowFieldKey,
    upsertFieldValue,
  ]);

  useEffect(() => {
    if (
      requestedMainTab === ORDER_DETAILS_MAIN_TABS.FILES &&
      isFilesTabAvailable &&
      !isAILoading &&
      headerData.orderActivityType === "Active"
    ) {
      setActiveMainTab(ORDER_DETAILS_MAIN_TABS.FILES);
    } else if (requestedMainTab === ORDER_DETAILS_MAIN_TABS.ORDER_INFO) {
      setActiveMainTab(ORDER_DETAILS_MAIN_TABS.ORDER_INFO);
    }
  }, [requestedMainTab, isFilesTabAvailable, isAILoading, headerData.orderActivityType]);

  useEffect(() => {
    if (!isFilesTabAvailable && activeMainTab === ORDER_DETAILS_MAIN_TABS.FILES) {
      setActiveMainTab(ORDER_DETAILS_MAIN_TABS.ORDER_INFO);
    }
  }, [activeMainTab, isFilesTabAvailable]);

  // Handle Files tab click - only enabled when analyzer is General Ledger Analyzer
  // and AI is not loading.
  const handleFilesTabClick = () => {
    if (
      isFilesTabAvailable &&
      !isAILoading &&
      headerData.orderActivityType === "Active"
    ) {
      setActiveMainTab(ORDER_DETAILS_MAIN_TABS.FILES);
      // Notify parent to trigger chat messages
      if (onFilesTabClick) {
        onFilesTabClick();
      }
    }
  };

  // Validates field value based on field validations config
  const validateField = useCallback(
    (fieldKey, value, dataType, allFieldValues = []) => {
      const baseFieldKey = getBaseFieldName(fieldKey);
      const validation = getFieldValidation(baseFieldKey);
      const fieldDefinition = fieldDefinitionsMap.get(fieldKey);
      const normalizedValue =
        typeof value === "string" ? value : String(value ?? "");
      const normalizedFieldValue = isIntegerOnlyField(baseFieldKey)
        ? normalizeIntegerFieldValue(normalizedValue)
        : normalizedValue;
      const regexValidationValue =
        validation?.regexTarget === "displayValue"
          ? getDropdownDisplayMatchValue(fieldDefinition, normalizedFieldValue)
          : normalizedFieldValue;
      const regexValidationValues =
        fieldDefinition?.dataType === "ActiveDirectory" ||
        isAdditionalDidField(fieldDefinition || baseFieldKey)
        ? normalizeListDisplayValue(value)
        : [regexValidationValue].filter(
            (candidateValue) => String(candidateValue ?? "").trim() !== "",
          );
      const isRequired = Boolean(
        !(isOrderValidationStatus && isContributorField(baseFieldKey)) &&
          (fieldDefinition?.isRequired ||
            validation?.required ||
            fieldDefinition?.required),
      );

      let errors = [];
      //New Added
      const requiredValidationMessage =
        validation?.requiredMessage ||
        fieldDefinition?.requiredMessage ||
        "This field is required";
      //New Added
      // Check required validation
      if (isRequired && isEmptyFieldValue(value)) {
        //New Added
        setFieldValidationErrors((prev) => ({
          ...prev,
          [fieldKey]: requiredValidationMessage,
        }));
        return false;
      }
      //New Added

      // If empty and not required, clear errors and return valid
      if (isEmptyFieldValue(value)) {
        setFieldValidationErrors((prev) => {
          const updated = { ...prev };
          delete updated[fieldKey];
          return updated;
        });
        return true;
        //}
      }

      // Contributor uniqueness validation
      if (isContributorField(baseFieldKey) && value) {
        const contributorValidation = validateContributorUniqueness(
          baseFieldKey,
          value,
          allFieldValues,
        );
        if (!contributorValidation.isValid) {
          errors.push(contributorValidation.message);
        }
      }

      if (dataType === "Date") {
        const dateValidationError = getDateValidationError(fieldKey, value);

        if (dateValidationError) {
          errors.push(dateValidationError);
        }
      }

      // Date relationship validation (cross-field validation for date fields)
      if (
        isDateRelationshipField(baseFieldKey) &&
        value &&
        dataType === "Date"
      ) {
        const dateRelationshipValidation = validateDateRelationships(
          baseFieldKey,
          value,
          allFieldValues,
          orderDetails,
        );
        if (!dateRelationshipValidation.isValid) {
          errors.push(dateRelationshipValidation.message);
        }
      }

      // If field has validation config, use it
      if (validation) {
        // Regex validation

        if (
          validation.regex &&
          regexValidationValues.some(
            (candidateValue) =>
              !validation.regex.test(String(candidateValue || "").trim()),
          )
        ) {
          errors.push(validation.message || "Invalid format");
        }

        // Min/Max for numbers
        if (validation.min !== undefined || validation.max !== undefined) {
          const numValue = parseFloat(normalizedFieldValue);
          if (!isNaN(numValue)) {
            if (validation.min !== undefined && numValue < validation.min) {
              errors.push(
                validation.message ||
                  `Value must be at least ${validation.min}`,
              );
            }
            if (validation.max !== undefined && numValue > validation.max) {
              errors.push(
                validation.message || `Value must be at most ${validation.max}`,
              );
            }
          }
        }

        // MinLength/MaxLength for text
        if (
          validation.minLength !== undefined &&
          normalizedFieldValue.length < validation.minLength
        ) {
          errors.push(
            validation.message ||
              `Minimum ${validation.minLength} characters required`,
          );
        }
        if (
          validation.maxLength !== undefined &&
          normalizedFieldValue.length > validation.maxLength
        ) {
          errors.push(
            validation.message ||
              `Maximum ${validation.maxLength} characters allowed`,
          );
        }

        // No past dates validation
        if (validation.noPastDates && dataType === "Date") {
          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (selectedDate < today) {
            errors.push(validation.message || "Date cannot be before today");
          }
        }
      } else {
        // Fallback to dataType-based validation if no config exists
        switch (dataType) {
          case "numerical": {
            const numericRegex = isIntegerOnlyField(baseFieldKey)
              ? /^\d*$/
              : /^-?\d*\.?\d*$/;
            if (!numericRegex.test(normalizedFieldValue)) {
              errors.push(
                isIntegerOnlyField(baseFieldKey)
                  ? "Only whole numbers are allowed"
                  : "Only numbers are allowed",
              );
            }
            break;
          }
          default:
            break;
        }
      }

      if (
        isRequired &&
        fieldDefinition?.dataType === "dropdown" &&
        !doesDropdownValueMatchOptions(fieldDefinition, normalizedFieldValue)
      ) {
        errors.push(INVALID_DROPDOWN_OPTION_MESSAGE);
      }

      // Update validation errors state
      if (errors.length > 0) {
        setFieldValidationErrors((prev) => ({
          ...prev,
          [fieldKey]: errors[0], // Show first error
        }));
        return false;
      } else {
        setFieldValidationErrors((prev) => {
          const updated = { ...prev };
          delete updated[fieldKey];
          return updated;
        });
        return true;
      }
    },
    [fieldDefinitionsMap, getDateValidationError, orderDetails, isOrderValidationStatus],
  );

  const validateEditableFieldsOnSave = useCallback(() => {
    const allCurrentFieldValues = Array.from(fieldDefinitionsMap.keys()).map(
      (fieldKey) => ({
        fieldName: fieldKey,
        updatedValue: getCurrentFieldValue(fieldKey),
      }),
    );

    let isValid = true;
    const invalidFieldKeys = [];

    fieldDefinitionsMap.forEach((fieldDefinition, fieldKey) => {
      // Previous implementation validated unmapped metadata/header fields
      // during Save. Only validate fields that belong to a user-facing tab.
      if (!fieldTabLabelMap.has(fieldKey)) {
        return;
      }

      if (!isEditableFieldRegionalLead(fieldDefinition)) {
        return;
      }

      if (
        fieldDefinition?.sectionName === SAVING_SECTION_NAME &&
        isSavingOtherReasonField(fieldDefinition) &&
        !shouldShowSavingOtherReasonField(fieldDefinition)
      ) {
        return;
      }

      if (
        isOtherOnHoldReasonField(fieldDefinition) &&
        !shouldShowOnHoldOtherReasonField(fieldDefinition)
      ) {
        return;
      }

      if (
        isOtherCleansingToolField(fieldDefinition) &&
        !shouldShowOtherCleansingToolField(fieldDefinition)
      ) {
        return;
      }

      if (
        isOtherAnalyzerTypeField(fieldDefinition) &&
        !shouldShowOtherAnalyzerTypeField(fieldDefinition)
      ) {
        return;
      }

      if (
        isOtherMarketSegmentField(fieldDefinition) &&
        !shouldShowOtherMarketSegmentField(fieldDefinition)
      ) {
        return;
      }

      if (
        isToolIssueDescriptionField(fieldDefinition) &&
        !shouldShowToolIssueDescriptionField(fieldDefinition)
      ) {
        return;
      }

      if (
        isReprocessDescriptionField(fieldDefinition) &&
        !shouldShowReprocessDescriptionField(fieldDefinition)
      ) {
        return;
      }

      if (
        isOtherErpField(fieldDefinition) &&
        !shouldShowOtherErpField(fieldDefinition)
      ) {
        return;
      }

      if (
        isOtherExtractionToolField(fieldDefinition) &&
        !shouldShowOtherExtractionToolField(fieldDefinition)
      ) {
        return;
      }

      if (
        isOtherCancellationReasonField(fieldDefinition) &&
        !shouldShowCancellationOtherReasonField(fieldDefinition)
      ) {
        return;
      }

      const currentValue = allCurrentFieldValues.find(
        (item) => item.fieldName === fieldKey,
      )?.updatedValue;

      const fieldIsValid = validateField(
        fieldKey,
        currentValue,
        fieldDefinition.dataType,
        allCurrentFieldValues,
      );

      if (!fieldIsValid) {
        isValid = false;
        invalidFieldKeys.push(fieldKey);
      }
    });

    return { isValid, invalidFieldKeys };
  }, [
    fieldDefinitionsMap,
    fieldTabLabelMap,
    getCurrentFieldValue,
    isEditableFieldRegionalLead,
    validateField,
    shouldShowCancellationOtherReasonField,
    shouldShowOtherAnalyzerTypeField,
    shouldShowOtherMarketSegmentField,
    shouldShowOtherErpField,
    shouldShowOtherExtractionToolField,
    shouldShowOtherCleansingToolField,
    shouldShowOnHoldOtherReasonField,
    shouldShowReprocessDescriptionField,
    shouldShowSavingOtherReasonField,
    shouldShowToolIssueDescriptionField,
  ]);

  const resolveValidationFieldKey = useCallback(
    (fieldKey) => {
      if (!fieldKey) {
        return "";
      }

      if (fieldDefinitionsMap.has(fieldKey)) {
        return fieldKey;
      }

      const baseFieldKey = getBaseFieldName(fieldKey);

      return (
        Array.from(fieldDefinitionsMap.keys()).find(
          (candidateKey) => getBaseFieldName(candidateKey) === baseFieldKey,
        ) || fieldKey
      );
    },
    [fieldDefinitionsMap],
  );

  const setContributorSelectionPending = useCallback(
    (fieldKey, hasPendingSelection) => {
      const resolvedFieldKey = resolveValidationFieldKey(fieldKey);

      setPendingContributorSelections((prev) => {
        const updated = { ...prev };

        delete updated[fieldKey];
        delete updated[resolvedFieldKey];

        if (hasPendingSelection) {
          updated[resolvedFieldKey] = true;
        }

        return updated;
      });
    },
    [resolveValidationFieldKey],
  );

  // Checks if a field has validation error
  const hasValidationError = useCallback(
    (fieldKey) => {
      const resolvedFieldKey = resolveValidationFieldKey(fieldKey);

      return (
        Object.prototype.hasOwnProperty.call(fieldValidationErrors, fieldKey) ||
        Object.prototype.hasOwnProperty.call(
          fieldValidationErrors,
          resolvedFieldKey,
        ) ||
        Object.prototype.hasOwnProperty.call(
          pendingContributorSelections,
          fieldKey,
        ) ||
        Object.prototype.hasOwnProperty.call(
          pendingContributorSelections,
          resolvedFieldKey,
        )
      );
    },
    [
      fieldValidationErrors,
      pendingContributorSelections,
      resolveValidationFieldKey,
    ],
  );

  // Gets validation error message for a field
  const getValidationErrorMessage = useCallback(
    (fieldKey) => {
      const resolvedFieldKey = resolveValidationFieldKey(fieldKey);

      const hasPendingSelection =
        pendingContributorSelections[fieldKey] ||
        pendingContributorSelections[resolvedFieldKey];

      return (
        fieldValidationErrors[fieldKey] ||
        fieldValidationErrors[resolvedFieldKey] ||
        (hasPendingSelection
          ? ACTIVE_DIRECTORY_SELECTION_REQUIRED_MESSAGE
          : "") ||
        ""
      );
    },
    [
      fieldValidationErrors,
      pendingContributorSelections,
      resolveValidationFieldKey,
    ],
  );

  const blockingValidationFieldKeys = useMemo(
    () =>
      Array.from(
        new Set([
          ...Object.keys(fieldValidationErrors),
          ...Object.keys(pendingContributorSelections),
        ]),
      ),
    [fieldValidationErrors, pendingContributorSelections],
  );

  const hasBlockingValidationErrors = useMemo(
    () => blockingValidationFieldKeys.length > 0,
    [blockingValidationFieldKeys],
  );

  const isChecklistReadOnlyRole =
    checklistRole === "Regional Lead" || checklistRole === "Approver";

  const isProjectCompleteStage = [
    headerData.currentSubStage || headerData.status,
    headerData.currentStage,
  ].some(
    (stage) => String(stage || "").trim().toLowerCase() === "project complete",
  );

  const isChecklistEditLocked =
    isChecklistReadOnlyRole || isProjectCompleteStage;

  const isSaveActionDisabled =
    (!isActiveOrder && !isEditing) ||
    (activeTab === ORDER_TABS.CHECKLIST && isChecklistEditLocked) ||
    (isEditing &&
      activeTab !== ORDER_TABS.CHECKLIST &&
      hasBlockingValidationErrors);

  // Get set of tab names that have validation errors
  const tabsWithErrors = useMemo(() => {
    const errorTabNames = new Set(
      blockingValidationFieldKeys
        .map((fieldKey) =>
          fieldTabLabelMap.get(resolveValidationFieldKey(fieldKey)),
        )
        .filter(Boolean),
    );
    return errorTabNames;
  }, [
    blockingValidationFieldKeys,
    fieldTabLabelMap,
    resolveValidationFieldKey,
  ]);

  const getValidationFieldLabels = useCallback(
    (fieldKeys = []) => {
      return Array.from(
        new Set(
          fieldKeys
            .map(resolveValidationFieldKey)
            .map((fieldKey) => {
              const fieldDefinition = fieldDefinitionsMap.get(fieldKey);

              return (
                fieldDefinition?.labelToShowOnUI ||
                fieldDefinition?.field ||
                getBaseFieldName(fieldKey)
              );
            })
            .filter(Boolean),
        ),
      );
    },
    [fieldDefinitionsMap, resolveValidationFieldKey],
  );

  const getValidationErrorToastMessage = useCallback(
    (invalidFieldKeys = []) => {
      const fieldKeysToResolve =
        invalidFieldKeys.length > 0
          ? invalidFieldKeys
          : blockingValidationFieldKeys;
      const resolvedFieldKeys = fieldKeysToResolve.map(
        resolveValidationFieldKey,
      );
      const fieldLabels = getValidationFieldLabels(resolvedFieldKeys);
      const fieldLabelSummary = fieldLabels.join(", ");

      const tabNames = Array.from(
        new Set(
          resolvedFieldKeys
            .map((fieldKey) => fieldTabLabelMap.get(fieldKey))
            .filter(Boolean),
        ),
      );

      if (tabNames.length === 0) {
        return fieldLabelSummary
          ? `There are validation errors in some fields. Please recheck and save again. Fields: ${fieldLabelSummary}.`
          : "There are validation errors in some fields. Please recheck and save again.";
      }

      return fieldLabelSummary
        ? `There are validation errors in some fields under: ${tabNames.join(", ")} tab(s). Please recheck and save again. Fields: ${fieldLabelSummary}.`
        : `There are validation errors in some fields under: ${tabNames.join(", ")} tab(s). Please recheck and save again.`;
    },
    [
      blockingValidationFieldKeys,
      fieldTabLabelMap,
      getValidationFieldLabels,
      resolveValidationFieldKey,
    ],
  );

  const revealValidationContext = useCallback(
    (invalidFieldKeys = []) => {
      const resolvedFieldKey = invalidFieldKeys
        .map(resolveValidationFieldKey)
        .find((fieldKey) => fieldDefinitionsMap.has(fieldKey));

      if (!resolvedFieldKey) {
        return;
      }

      const targetTab = fieldTabLabelMap.get(resolvedFieldKey);
      const fieldDefinition = fieldDefinitionsMap.get(resolvedFieldKey);

      if (
        !showAllTabs &&
        (targetTab === ORDER_TABS.ADDITIONAL || targetTab === ORDER_TABS.OTHER)
      ) {
        setShowAllTabs(true);
      }

      if (targetTab) {
        setActiveTab(targetTab);
      }

      if (targetTab === ORDER_TABS.OTHER) {
        const sectionDisplayName =
          fieldDefinition?.sectionName === EFFORT_SECTION_NAME
            ? OTHER_SECTION_DISPLAY_NAMES.EFFORTS
            : fieldDefinition?.sectionName === SAVING_SECTION_NAME
              ? OTHER_SECTION_DISPLAY_NAMES.SAVINGS
              : "";

        if (sectionDisplayName) {
          setExpandedSections((prev) => ({
            ...prev,
            [sectionDisplayName]: true,
          }));
        }
      }
    },
    [
      fieldDefinitionsMap,
      fieldTabLabelMap,
      resolveValidationFieldKey,
      showAllTabs,
    ],
  );

  // Handle cancel edit - closes edit mode without saving and resets to original API values
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setFieldValues([]); // Clear edited values to revert to original API values
    setIsAnalyzerTypeChanged(false);
    setShowAnalyzerTypeToast(false);
    setDraftOtherSectionRows({});
    setRemovedOtherSectionRowKeys([]);
    nextDraftOtherSectionRowIdRef.current = 0;
    setFieldValidationErrors({});
    setPendingContributorSelections({});
    setErrorFieldArray([]);
    clearErrorToast();
    checklistResetRef.current?.(); // Discard unsaved checklist answers/comments
  }, [clearErrorToast]);

  const handleSubTabChange = useCallback(
    (targetTab) => {
      if (isAnalyzerTypeChanged && targetTab !== activeTab) {
        setShowAnalyzerTypeToast(true);
        return;
      }

      setActiveTab(targetTab);
    },
    [activeTab, isAnalyzerTypeChanged],
  );

  // Returns fields configuration for the active tab
  const currentTabFields = useMemo(() => {
    if (activeTab === ORDER_TABS.OTHER) {
      return [];
    }

    const fields = normalizedOrderFields.filter((field) => {
      if (field?.rowKey) {
        return false;
      }

      if (SECTION_TO_TAB_LABEL[field?.sectionName] !== activeTab) {
        return false;
      }

      if (
        isOtherOnHoldReasonField(field) &&
        !shouldShowOnHoldOtherReasonField(field)
      ) {
        return false;
      }

      if (
        isOtherCleansingToolField(field) &&
        !shouldShowOtherCleansingToolField(field)
      ) {
        return false;
      }

      if (
        isOtherAnalyzerTypeField(field) &&
        !shouldShowOtherAnalyzerTypeField(field)
      ) {
        return false;
      }

      if (
        isOtherMarketSegmentField(field) &&
        !shouldShowOtherMarketSegmentField(field)
      ) {
        return false;
      }

      if (
        isToolIssueDescriptionField(field) &&
        !shouldShowToolIssueDescriptionField(field)
      ) {
        return false;
      }

      if (
        isReprocessDescriptionField(field) &&
        !shouldShowReprocessDescriptionField(field)
      ) {
        return false;
      }

      if (isOtherErpField(field) && !shouldShowOtherErpField(field)) {
        return false;
      }

      if (
        isOtherExtractionToolField(field) &&
        !shouldShowOtherExtractionToolField(field)
      ) {
        return false;
      }

      if (
        isOtherCancellationReasonField(field) &&
        !shouldShowCancellationOtherReasonField(field)
      ) {
        return false;
      }

      if (activeTab !== ORDER_TABS.CONTRIBUTORS) {
        return true;
      }

      const normalizedLabel = String(field?.labelToShowOnUI || "")
        .trim()
        .toLowerCase();

      return !HIDDEN_CONTRIBUTOR_FIELD_LABELS.has(normalizedLabel);
    });

    if (showAllTabs) {
      return fields;
    } else {
      return fields.filter((field) => field.isShowAll === false);
    }
  }, [
    activeTab,
    normalizedOrderFields,
    showAllTabs,
    shouldShowCancellationOtherReasonField,
    shouldShowOtherAnalyzerTypeField,
    shouldShowOtherMarketSegmentField,
    shouldShowOtherErpField,
    shouldShowOtherExtractionToolField,
    shouldShowOtherCleansingToolField,
    shouldShowOnHoldOtherReasonField,
    shouldShowReprocessDescriptionField,
    shouldShowToolIssueDescriptionField,
  ]);

  // Returns sections configuration for the "Other" tab from API response
  const currentTabSections = useMemo(() => {
    if (activeTab !== ORDER_TABS.OTHER) return null;

    const sections = [];
    const getVisibleRows = (rows) =>
      rows
        .map((row) => ({
          ...row,
          fields: row.fields
            .map((field) => getResolvedFieldDefinition(field))
            .filter((field) => {
              if (!showAllTabs && field.isShowAll !== false) {
                return false;
              }

              if (
                field.sectionName === SAVING_SECTION_NAME &&
                isSavingOtherReasonField(field) &&
                !shouldShowSavingOtherReasonField(field)
              ) {
                return false;
              }

              if (
                isOtherCancellationReasonField(field) &&
                !shouldShowCancellationOtherReasonField(field)
              ) {
                return false;
              }

              if (
                isOtherOnHoldReasonField(field) &&
                !shouldShowOnHoldOtherReasonField(field)
              ) {
                return false;
              }

              if (
                isOtherCleansingToolField(field) &&
                !shouldShowOtherCleansingToolField(field)
              ) {
                return false;
              }

              if (
                isOtherAnalyzerTypeField(field) &&
                !shouldShowOtherAnalyzerTypeField(field)
              ) {
                return false;
              }

              if (
                isOtherMarketSegmentField(field) &&
                !shouldShowOtherMarketSegmentField(field)
              ) {
                return false;
              }

              if (
                isToolIssueDescriptionField(field) &&
                !shouldShowToolIssueDescriptionField(field)
              ) {
                return false;
              }

              if (
                isReprocessDescriptionField(field) &&
                !shouldShowReprocessDescriptionField(field)
              ) {
                return false;
              }

              if (isOtherErpField(field) && !shouldShowOtherErpField(field)) {
                return false;
              }

              if (
                isOtherExtractionToolField(field) &&
                !shouldShowOtherExtractionToolField(field)
              ) {
                return false;
              }

              return true;
            }),
        }))
        .filter((row) => row.fields.length > 0);

    const visibleEffortRows = getVisibleRows(effortSectionRows);
    const visibleSavingRows = getVisibleRows(savingSectionRows);

    if (otherSectionConfigsBySection.has(EFFORT_SECTION_NAME)) {
      sections.push({
        sectionName: OTHER_SECTION_DISPLAY_NAMES.EFFORTS,
        rows: visibleEffortRows,
      });
    }

    if (otherSectionConfigsBySection.has(SAVING_SECTION_NAME)) {
      sections.push({
        sectionName: OTHER_SECTION_DISPLAY_NAMES.SAVINGS,
        rows: visibleSavingRows,
      });
    }

    if (sections.length === 0) {
      return null;
    }

    return sections;
  }, [
    activeTab,
    effortSectionRows,
    getResolvedFieldDefinition,
    otherSectionConfigsBySection,
    savingSectionRows,
    showAllTabs,
    shouldShowCancellationOtherReasonField,
    shouldShowOtherAnalyzerTypeField,
    shouldShowOtherMarketSegmentField,
    shouldShowOtherErpField,
    shouldShowOtherExtractionToolField,
    shouldShowOtherCleansingToolField,
    shouldShowOnHoldOtherReasonField,
    shouldShowReprocessDescriptionField,
    shouldShowSavingOtherReasonField,
    shouldShowToolIssueDescriptionField,
  ]);

  // Toggles accordion section visibility
  const toggleSection = useCallback((sectionName) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  }, []);

  const getDraftOtherSectionFieldInitialValue = useCallback(() => {
    return "";
  }, []);

  const createDraftOtherSectionRow = useCallback(
    (sectionName) => {
      const templateRows = baseOtherSectionRowsBySection.get(sectionName) || [];
      const templateRow = templateRows[templateRows.length - 1] || null;
      const templateFields = templateRow
        ? templateRow.fields
        : Array.isArray(
              otherSectionConfigsBySection.get(sectionName)?.pagefields,
            )
          ? otherSectionConfigsBySection.get(sectionName).pagefields
          : [];

      if (templateFields.length === 0) {
        return null;
      }

      const existingRowsCount =
        templateRows.length + (draftOtherSectionRows[sectionName] || []).length;
      const draftRowId = `draft-${++nextDraftOtherSectionRowIdRef.current}`;
      const rowKey = buildSectionRowKey(
        sectionName,
        draftRowId,
        existingRowsCount,
      );
      const rowDataInfo = {};
      const fields = templateFields.map((field) => {
        const baseFieldName = String(
          field?.baseFieldName || field?.field || "",
        );
        const isSavingsReasonField =
          sectionName === SAVING_SECTION_NAME &&
          doesFieldMatchCandidates(
            field,
            SAVING_PAYLOAD_FIELD_CANDIDATES.reasonId,
          );

        return {
          ...field,
          sectionName, // Ensure sectionName is set for draft fields
          isEditable: true, // Draft fields are always editable
          fieldValue: getDraftOtherSectionFieldInitialValue(sectionName, field),
          rowId: draftRowId,
          rowIndex: existingRowsCount,
          rowKey,
          rowDataInfo,
          baseFieldName,
          fieldStateKey: buildFieldStateKey(
            sectionName,
            draftRowId,
            existingRowsCount,
            baseFieldName,
            true,
          ),
          fieldValueList: isSavingsReasonField ? [] : field?.fieldValueList,
          dropdownReason: isSavingsReasonField ? [] : field?.dropdownReason,
        };
      });

      return {
        rowId: draftRowId,
        rowIndex: existingRowsCount,
        rowKey,
        pagefieldsListInfo: fields,
        rowDataInfo,
        fields,
      };
    },
    [
      baseOtherSectionRowsBySection,
      draftOtherSectionRows,
      getDraftOtherSectionFieldInitialValue,
      otherSectionConfigsBySection,
    ],
  );

  const canAddOtherSectionRow = useCallback(
    (sectionDisplayName) => {
      if (!isEditing) {
        return false;
      }

      const sectionName =
        OTHER_SECTION_API_NAME_BY_DISPLAY_NAME[sectionDisplayName] || "";

      if (!sectionName) {
        return false;
      }

      const hasTemplateFields =
        (otherSectionConfigsBySection.get(sectionName)?.pagefields || [])
          .length > 0;
      const hasBaseRows =
        (baseOtherSectionRowsBySection.get(sectionName) || []).length > 0;

      return hasTemplateFields || hasBaseRows;
    },
    [baseOtherSectionRowsBySection, isEditing, otherSectionConfigsBySection],
  );

  const handleAddOtherSectionRow = useCallback(
    (sectionDisplayName) => {
      const sectionName =
        OTHER_SECTION_API_NAME_BY_DISPLAY_NAME[sectionDisplayName] || "";

      if (!sectionName) {
        return;
      }

      const nextRow = createDraftOtherSectionRow(sectionName);

      if (!nextRow) {
        return;
      }

      setDraftOtherSectionRows((prev) => ({
        ...prev,
        [sectionName]: [...(prev[sectionName] || []), nextRow],
      }));
      setExpandedSections((prev) => ({
        ...prev,
        [sectionDisplayName]: true,
      }));
    },
    [createDraftOtherSectionRow],
  );

  const handleRemoveOtherSectionRow = useCallback(
    async (sectionName, rowKey) => {
      const row = sectionRowsByKey.get(rowKey);
      const isDraftRow = String(row?.rowId || "").startsWith("draft-");

      if (isDraftRow) {
        setDraftOtherSectionRows((prev) => ({
          ...prev,
          [sectionName]: (prev[sectionName] || []).filter(
            (draftRow) => draftRow.rowKey !== rowKey,
          ),
        }));
        return;
      }

      const isSavingsSection =
        sectionName === SAVING_SECTION_NAME || sectionName === "Savings";
      const isEffortsSection =
        sectionName === EFFORT_SECTION_NAME || sectionName === "Efforts";
      const idCandidates =
        isSavingsSection
          ? SAVING_PAYLOAD_FIELD_CANDIDATES.id
          : ["EffortForId"];
      const recordId = getRowDataValueByCandidates(
        row?.rowDataInfo,
        idCandidates,
      );
      const orderId = headerData.requestId || headerData.orderId;

      if (!orderId || recordId === "" || recordId == null) {
        console.error("Unable to delete Other tab row: missing order or record ID.");
        return;
      }

      try {
        if (isSavingsSection) {
          await OrdersService.deleteSavingsList(orderId, [recordId]);
        } else if (isEffortsSection) {
          await OrdersService.deleteEffortList(orderId, [recordId]);
        } else {
          return;
        }

        setRemovedOtherSectionRowKeys((prev) => [...prev, rowKey]);
        onRefreshOrderDetails?.();
      } catch (error) {
        console.error("Unable to delete Other tab row:", error);
      }
    },
    [
      headerData.orderId,
      headerData.requestId,
      onRefreshOrderDetails,
      sectionRowsByKey,
    ],
  );

  const getDropdownOptions = useCallback((field) => {
    return getDropdownFieldOptions(field);
  }, []);

  const getDropdownDisplayValue = useCallback(
    (field, value) => getDropdownDisplayMatchValue(field, value),

    [],
  );

  const getFieldValueForSave = useCallback(
    (fieldKey, currentValue) => {
      if (isIntegerOnlyField(fieldKey)) {
        return normalizeIntegerFieldValue(currentValue);
      }

      const fieldDefinition = fieldDefinitionsMap.get(fieldKey);

      if (fieldDefinition?.dataType === "ActiveDirectory") {
        return normalizeListDisplayValue(currentValue).join(",");
      }

      if (!fieldDefinition || fieldDefinition.dataType !== "dropdown") {
        return currentValue;
      }

      const selectedDropdownValue = resolveFieldOptionValue(
        fieldDefinition,
        currentValue,
      );

      if (
        fieldDefinition.sectionName === SAVING_SECTION_NAME &&
        (doesFieldMatchCandidates(
          fieldDefinition,
          SAVING_PAYLOAD_FIELD_CANDIDATES.applicationUsedId,
        ) ||
          doesFieldMatchCandidates(
            fieldDefinition,
            SAVING_PAYLOAD_FIELD_CANDIDATES.reasonId,
          ))
      ) {
        return selectedDropdownValue || currentValue;
      }

      const selectedDropdownLabel = getDropdownDisplayValue(
        fieldDefinition,
        selectedDropdownValue,
      );

      return selectedDropdownLabel || currentValue;
    },
    [fieldDefinitionsMap, getDropdownDisplayValue, resolveFieldOptionValue],
  );

  const getSnapshotFieldValueForSave = useCallback(
    (fieldKey, updatedFieldValues, fallbackValue = "") => {
      if (!fieldKey) {
        return fallbackValue;
      }

      const rawValue = getFieldValueFromSnapshot(fieldKey, updatedFieldValues);
      const normalizedValue = getFieldValueForSave(fieldKey, rawValue);

      if (normalizedValue === undefined || normalizedValue === null) {
        return fallbackValue;
      }

      const stringValue = String(normalizedValue);
      return stringValue || fallbackValue;
    },
    [getFieldValueForSave, getFieldValueFromSnapshot],
  );

  const hasEffortSection = useMemo(
    () => effortSectionRows.length > 0,
    [effortSectionRows],
  );

  const hasSavingSection = useMemo(
    () => savingSectionRows.length > 0,
    [savingSectionRows],
  );

  // Updates field value in edit mode with validation
  const handleFieldChange = useCallback(
    (field, newValue, dataType) => {
      console.log("Field change detected:", field, newValue, dataType);
      const fieldKey =
        typeof field === "string" ? field : getFieldStateKey(field);
      const baseFieldKey = getBaseFieldName(field);

      if (baseFieldKey.toLowerCase() === "helixanalyzerid") {
        setIsAnalyzerTypeChanged(true);
        setShowAnalyzerTypeToast(true);
      }
      const rowKey = typeof field === "string" ? "" : field?.rowKey || "";
      const sectionName =
        typeof field === "string"
          ? fieldSectionMap.get(fieldKey) || ""
          : field?.sectionName || fieldSectionMap.get(fieldKey) || "";
      const currentRow = rowKey ? sectionRowsByKey.get(rowKey) : null;
      const normalizedChangeValue =
        newValue && typeof newValue === "object" && "target" in newValue
          ? (newValue.target?.value ?? "")
          : newValue;
      const resolvedValue = isIntegerOnlyField(baseFieldKey)
        ? normalizeIntegerFieldValue(normalizedChangeValue)
        : normalizedChangeValue;

      let updatedFieldValues = upsertFieldValue(
        fieldValues,
        fieldKey,
        resolvedValue,
      );

      const setFieldValueInSnapshot = (targetFieldKey, targetValue) => {
        if (!targetFieldKey) {
          return;
        }

        updatedFieldValues = upsertFieldValue(
          updatedFieldValues,
          targetFieldKey,
          targetValue,
        );
      };

      const clearFieldErrors = (fieldKeys = []) => {
        setFieldValidationErrors((prev) => {
          const updated = { ...prev };

          fieldKeys.filter(Boolean).forEach((targetFieldKey) => {
            delete updated[targetFieldKey];
          });

          return updated;
        });
      };

      const fieldDefinition =
        typeof field === "string"
          ? fieldDefinitionsMap.get(fieldKey) || null
          : field;

      const clearDependentLocationFields = (candidateGroups = []) => {
        const dependentFieldKeys = candidateGroups
          .map((candidates) => getLocationFieldStateKeyByCandidates(candidates))
          .filter(
            (targetFieldKey) => targetFieldKey && targetFieldKey !== fieldKey,
          );

        dependentFieldKeys.forEach((targetFieldKey) => {
          setFieldValueInSnapshot(targetFieldKey, "");
        });

        clearFieldErrors(dependentFieldKeys);
      };

      const logRegionCountryListForSuperRegionChange = (
        selectedSuperRegionValue,
      ) => {
        const areaFieldKey = getLocationFieldStateKeyByCandidates(
          LOCATION_LOOKUP_FIELD_CANDIDATES.area,
        );
        const selectedAreaValue = getFieldValueFromSnapshot(
          areaFieldKey,
          updatedFieldValues,
        );
        const matchedRegion = findMatchedLocationRecord(
          locationHierarchyLookupData,
          selectedAreaValue,
          buildRegionLookupOption,
        );
        const matchedSuperRegion = findMatchedLocationRecord(
          matchedRegion?.superRegions || [],
          selectedSuperRegionValue,
          buildSuperRegionLookupOption,
        );

        console.log(
          "[OrdersDetailsDashboard] Region/Country list for selected Super Region",
          matchedSuperRegion?.countries || [],
        );
      };

      const syncEffortResourceNameForRow = (row, selectedRole) => {
        const roleFieldKey = getSectionRowFieldKey(
          row,
          EFFORT_ROW_FIELD_CANDIDATES.role,
        );
        const resourceNameFieldKey = getSectionRowFieldKey(
          row,
          EFFORT_ROW_FIELD_CANDIDATES.resourceName,
        );

        if (!roleFieldKey || !resourceNameFieldKey) {
          return;
        }

        const contributorFieldKey = getEffortContributorFieldKey(
          roleFieldKey,
          selectedRole,
        );

        if (!contributorFieldKey) {
          return;
        }

        const contributorValue = getFieldValueFromSnapshot(
          contributorFieldKey,
          updatedFieldValues,
        );

        setFieldValueInSnapshot(
          resourceNameFieldKey,
          isEmptyFieldValue(contributorValue) ? "" : contributorValue,
        );
        validateField(
          resourceNameFieldKey,
          isEmptyFieldValue(contributorValue) ? "" : contributorValue,
          fieldDefinitionsMap.get(resourceNameFieldKey)?.dataType || "Text",
          updatedFieldValues,
        );
      };

      if (
        fieldDefinition &&
        doesFieldMatchLocationCandidates(
          fieldDefinition,
          LOCATION_LOOKUP_FIELD_CANDIDATES.area,
        )
      ) {
        clearDependentLocationFields([
          LOCATION_LOOKUP_FIELD_CANDIDATES.superRegion,
          LOCATION_LOOKUP_FIELD_CANDIDATES.country,
          LOCATION_LOOKUP_FIELD_CANDIDATES.office,
        ]);
      } else if (
        fieldDefinition &&
        doesFieldMatchCandidates(
          fieldDefinition,
          LOCATION_LOOKUP_FIELD_CANDIDATES.superRegion,
        )
      ) {
        clearDependentLocationFields([
          LOCATION_LOOKUP_FIELD_CANDIDATES.country,
          LOCATION_LOOKUP_FIELD_CANDIDATES.office,
        ]);
        logRegionCountryListForSuperRegionChange(resolvedValue);
      } else if (
        fieldDefinition &&
        doesFieldMatchCandidates(
          fieldDefinition,
          LOCATION_LOOKUP_FIELD_CANDIDATES.country,
        )
      ) {
        clearDependentLocationFields([LOCATION_LOOKUP_FIELD_CANDIDATES.office]);
      }

      if (
        sectionName === EFFORT_SECTION_NAME &&
        currentRow &&
        baseFieldKey === "Role"
      ) {
        syncEffortResourceNameForRow(currentRow, resolvedValue);
      }

      if (Object.values(CONTRIBUTOR_ROLE_FIELD_MAP).includes(baseFieldKey)) {
        effortSectionRows.forEach((row) => {
          const roleFieldKey = getSectionRowFieldKey(
            row,
            EFFORT_ROW_FIELD_CANDIDATES.role,
          );

          if (!roleFieldKey) {
            return;
          }

          const selectedEffortRole = getFieldValueFromSnapshot(
            roleFieldKey,
            updatedFieldValues,
          );
          const mappedContributorFieldKey = getEffortContributorFieldKey(
            roleFieldKey,
            selectedEffortRole,
          );

          if (mappedContributorFieldKey === baseFieldKey) {
            syncEffortResourceNameForRow(row, selectedEffortRole);
          }
        });
      }

      if (
        sectionName === SAVING_SECTION_NAME &&
        currentRow &&
        baseFieldKey === "ApplicationUsedId"
      ) {
        const reasonFieldKey = getSectionRowFieldKey(
          currentRow,
          SAVING_PAYLOAD_FIELD_CANDIDATES.reasonId,
        );
        const otherReasonFieldKey = getSectionRowFieldKey(
          currentRow,
          SAVING_ROW_FIELD_CANDIDATES.otherReason,
        );
        const hoursFieldKey = getSectionRowFieldKey(
          currentRow,
          SAVING_PAYLOAD_FIELD_CANDIDATES.hoursToSave,
        );

        setFieldValueInSnapshot(reasonFieldKey, "");
        setFieldValueInSnapshot(otherReasonFieldKey, "");

        const matchedApplicationLookup =
          findMatchedApplicationUsedHoursReasonEntry(
            fieldDefinition || field,
            resolvedValue,
          );
        const defaultHoursValue = formatSavingDefaultHoursValue(
          matchedApplicationLookup?.defaultHours,
        );

        if (defaultHoursValue) {
          setFieldValueInSnapshot(hoursFieldKey, defaultHoursValue);
        }

        clearFieldErrors(
          defaultHoursValue
            ? [reasonFieldKey, otherReasonFieldKey, hoursFieldKey]
            : [reasonFieldKey, otherReasonFieldKey],
        );
      }

      if (
        sectionName === SAVING_SECTION_NAME &&
        currentRow &&
        baseFieldKey === "ReasonId"
      ) {
        const otherReasonFieldKey = getSectionRowFieldKey(
          currentRow,
          SAVING_ROW_FIELD_CANDIDATES.otherReason,
        );
        const selectedReasonLabel = getSavingReasonOptionsForRow(rowKey).find(
          (option) =>
            String(option?.value || "") === String(resolvedValue || ""),
        )?.field;

        if (
          String(selectedReasonLabel || "")
            .trim()
            .toLowerCase() !== "others"
        ) {
          setFieldValueInSnapshot(otherReasonFieldKey, "");
          clearFieldErrors([otherReasonFieldKey]);
        }
      }

      if (fieldDefinition && isOnHoldReasonField(fieldDefinition)) {
        const otherOnHoldReasonField = Array.from(
          fieldDefinitionsMap.values(),
        ).find((candidateField) => {
          if (!isOtherOnHoldReasonField(candidateField)) {
            return false;
          }

          if (
            fieldDefinition?.sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== fieldDefinition.sectionName
          ) {
            return false;
          }

          if (
            fieldDefinition?.rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== fieldDefinition.rowKey
          ) {
            return false;
          }

          return true;
        });
        const otherOnHoldReasonFieldKey = getFieldStateKey(
          otherOnHoldReasonField,
        );
        const resolvedOnHoldReasonValue =
          resolveFieldOptionValue(fieldDefinition, resolvedValue) ||
          resolvedValue;
        const matchingOnHoldReasonOption = getDropdownFieldOptions(
          fieldDefinition,
        ).find((option) =>
          doesDropdownOptionMatchValue(option, resolvedOnHoldReasonValue),
        );
        const selectedOnHoldReasonLabel = matchingOnHoldReasonOption
          ? getDropdownFieldOptionLabel(matchingOnHoldReasonOption)
          : resolvedValue;

        if (
          otherOnHoldReasonFieldKey &&
          !isOtherLikeOptionValue(selectedOnHoldReasonLabel) &&
          !isOtherLikeOptionValue(resolvedOnHoldReasonValue)
        ) {
          setFieldValueInSnapshot(otherOnHoldReasonFieldKey, "");
          clearFieldErrors([otherOnHoldReasonFieldKey]);
        }
      }

      if (fieldDefinition && isCleansingToolField(fieldDefinition)) {
        const otherCleansingToolField = Array.from(
          fieldDefinitionsMap.values(),
        ).find((candidateField) => {
          if (!isOtherCleansingToolField(candidateField)) {
            return false;
          }

          if (
            fieldDefinition?.sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== fieldDefinition.sectionName
          ) {
            return false;
          }

          if (
            fieldDefinition?.rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== fieldDefinition.rowKey
          ) {
            return false;
          }

          return true;
        });
        const otherCleansingToolFieldKey = getFieldStateKey(
          otherCleansingToolField,
        );
        const resolvedCleansingToolValue =
          resolveFieldOptionValue(fieldDefinition, resolvedValue) ||
          resolvedValue;
        const matchingCleansingToolOption = getDropdownFieldOptions(
          fieldDefinition,
        ).find((option) =>
          doesDropdownOptionMatchValue(option, resolvedCleansingToolValue),
        );
        const selectedCleansingToolLabel = matchingCleansingToolOption
          ? getDropdownFieldOptionLabel(matchingCleansingToolOption)
          : resolvedValue;

        if (
          otherCleansingToolFieldKey &&
          !isOtherLikeOptionValue(selectedCleansingToolLabel) &&
          !isOtherLikeOptionValue(resolvedCleansingToolValue)
        ) {
          setFieldValueInSnapshot(otherCleansingToolFieldKey, "");
          clearFieldErrors([otherCleansingToolFieldKey]);
        }
      }

      if (fieldDefinition && isAnalyzerTypeField(fieldDefinition)) {
        const otherAnalyzerTypeField = Array.from(
          fieldDefinitionsMap.values(),
        ).find((candidateField) => {
          if (!isOtherAnalyzerTypeField(candidateField)) {
            return false;
          }

          if (
            fieldDefinition?.sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== fieldDefinition.sectionName
          ) {
            return false;
          }

          if (
            fieldDefinition?.rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== fieldDefinition.rowKey
          ) {
            return false;
          }

          return true;
        });
        const otherAnalyzerTypeFieldKey = getFieldStateKey(
          otherAnalyzerTypeField,
        );
        const resolvedAnalyzerTypeValue =
          resolveFieldOptionValue(fieldDefinition, resolvedValue) ||
          resolvedValue;
        const matchingAnalyzerTypeOption = getDropdownFieldOptions(
          fieldDefinition,
        ).find((option) =>
          doesDropdownOptionMatchValue(option, resolvedAnalyzerTypeValue),
        );
        const selectedAnalyzerTypeLabel = matchingAnalyzerTypeOption
          ? getDropdownFieldOptionLabel(matchingAnalyzerTypeOption)
          : resolvedValue;

        if (
          otherAnalyzerTypeFieldKey &&
          !isOtherLikeOptionValue(selectedAnalyzerTypeLabel) &&
          !isOtherLikeOptionValue(resolvedAnalyzerTypeValue)
        ) {
          setFieldValueInSnapshot(otherAnalyzerTypeFieldKey, "");
          clearFieldErrors([otherAnalyzerTypeFieldKey]);
        }
      }

      if (fieldDefinition && isMarketSegmentField(fieldDefinition)) {
        const otherMarketSegmentField = Array.from(
          fieldDefinitionsMap.values(),
        ).find((candidateField) => {
          if (!isOtherMarketSegmentField(candidateField)) {
            return false;
          }

          if (
            fieldDefinition?.sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== fieldDefinition.sectionName
          ) {
            return false;
          }

          if (
            fieldDefinition?.rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== fieldDefinition.rowKey
          ) {
            return false;
          }

          return true;
        });
        const otherMarketSegmentFieldKey = getFieldStateKey(
          otherMarketSegmentField,
        );
        const resolvedMarketSegmentValue =
          resolveFieldOptionValue(fieldDefinition, resolvedValue) ||
          resolvedValue;
        const matchingMarketSegmentOption = getDropdownFieldOptions(
          fieldDefinition,
        ).find((option) =>
          doesDropdownOptionMatchValue(option, resolvedMarketSegmentValue),
        );
        const selectedMarketSegmentLabel = matchingMarketSegmentOption
          ? getDropdownFieldOptionLabel(matchingMarketSegmentOption)
          : resolvedValue;

        if (
          otherMarketSegmentFieldKey &&
          !isOtherLikeOptionValue(selectedMarketSegmentLabel) &&
          !isOtherLikeOptionValue(resolvedMarketSegmentValue)
        ) {
          setFieldValueInSnapshot(otherMarketSegmentFieldKey, "");
          clearFieldErrors([otherMarketSegmentFieldKey]);
        }
      }

      if (fieldDefinition && isToolIssueTypeField(fieldDefinition)) {
        const toolIssueDescriptionField = Array.from(
          fieldDefinitionsMap.values(),
        ).find((candidateField) => {
          if (!isToolIssueDescriptionField(candidateField)) {
            return false;
          }

          if (
            fieldDefinition?.sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== fieldDefinition.sectionName
          ) {
            return false;
          }

          if (
            fieldDefinition?.rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== fieldDefinition.rowKey
          ) {
            return false;
          }

          return true;
        });
        const toolIssueDescriptionFieldKey = getFieldStateKey(
          toolIssueDescriptionField,
        );
        const resolvedToolIssueTypeValue =
          resolveFieldOptionValue(fieldDefinition, resolvedValue) ||
          resolvedValue;
        const matchingToolIssueTypeOption = getDropdownFieldOptions(
          fieldDefinition,
        ).find((option) =>
          doesDropdownOptionMatchValue(option, resolvedToolIssueTypeValue),
        );
        const selectedToolIssueTypeLabel = matchingToolIssueTypeOption
          ? getDropdownFieldOptionLabel(matchingToolIssueTypeOption)
          : resolvedValue;

        if (
          toolIssueDescriptionFieldKey &&
          !isToolIssueDescriptionTriggerValue(selectedToolIssueTypeLabel) &&
          !isToolIssueDescriptionTriggerValue(resolvedToolIssueTypeValue)
        ) {
          setFieldValueInSnapshot(toolIssueDescriptionFieldKey, "");
          clearFieldErrors([toolIssueDescriptionFieldKey]);
        }
      }

      if (fieldDefinition && isReprocessReasonField(fieldDefinition)) {
        const reprocessDescriptionField = Array.from(
          fieldDefinitionsMap.values(),
        ).find((candidateField) => {
          if (!isReprocessDescriptionField(candidateField)) {
            return false;
          }

          if (
            fieldDefinition?.sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== fieldDefinition.sectionName
          ) {
            return false;
          }

          if (
            fieldDefinition?.rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== fieldDefinition.rowKey
          ) {
            return false;
          }

          return true;
        });
        const reprocessDescriptionFieldKey = getFieldStateKey(
          reprocessDescriptionField,
        );
        const resolvedReprocessReasonValue =
          resolveFieldOptionValue(fieldDefinition, resolvedValue) ||
          resolvedValue;
        const matchingReprocessReasonOption = getDropdownFieldOptions(
          fieldDefinition,
        ).find((option) =>
          doesDropdownOptionMatchValue(option, resolvedReprocessReasonValue),
        );
        const selectedReprocessReasonLabel = matchingReprocessReasonOption
          ? getDropdownFieldOptionLabel(matchingReprocessReasonOption)
          : resolvedValue;

        if (
          reprocessDescriptionFieldKey &&
          !isReprocessDescriptionTriggerValue(selectedReprocessReasonLabel) &&
          !isReprocessDescriptionTriggerValue(resolvedReprocessReasonValue)
        ) {
          setFieldValueInSnapshot(reprocessDescriptionFieldKey, "");
          clearFieldErrors([reprocessDescriptionFieldKey]);
        }
      }

      if (fieldDefinition && isErpNameField(fieldDefinition)) {
        const otherErpField = Array.from(fieldDefinitionsMap.values()).find(
          (candidateField) => {
            if (!isOtherErpField(candidateField)) {
              return false;
            }

            if (
              fieldDefinition?.sectionName &&
              candidateField?.sectionName &&
              candidateField.sectionName !== fieldDefinition.sectionName
            ) {
              return false;
            }

            if (
              fieldDefinition?.rowKey &&
              candidateField?.rowKey &&
              candidateField.rowKey !== fieldDefinition.rowKey
            ) {
              return false;
            }

            return true;
          },
        );
        const otherErpFieldKey = getFieldStateKey(otherErpField);
        const resolvedErpValue =
          resolveFieldOptionValue(fieldDefinition, resolvedValue) ||
          resolvedValue;
        const matchingErpOption = getDropdownFieldOptions(fieldDefinition).find(
          (option) => doesDropdownOptionMatchValue(option, resolvedErpValue),
        );
        const selectedErpLabel = matchingErpOption
          ? getDropdownFieldOptionLabel(matchingErpOption)
          : resolvedValue;

        if (
          otherErpFieldKey &&
          !isOtherErpTriggerValue(selectedErpLabel) &&
          !isOtherErpTriggerValue(resolvedErpValue)
        ) {
          setFieldValueInSnapshot(otherErpFieldKey, "");
          clearFieldErrors([otherErpFieldKey]);
        }
      }

      if (fieldDefinition && isExtractionToolField(fieldDefinition)) {
        const otherExtractionToolField = Array.from(
          fieldDefinitionsMap.values(),
        ).find((candidateField) => {
          if (!isOtherExtractionToolField(candidateField)) {
            return false;
          }

          if (
            fieldDefinition?.sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== fieldDefinition.sectionName
          ) {
            return false;
          }

          if (
            fieldDefinition?.rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== fieldDefinition.rowKey
          ) {
            return false;
          }

          return true;
        });
        const otherExtractionToolFieldKey = getFieldStateKey(
          otherExtractionToolField,
        );
        const resolvedExtractionToolValue =
          resolveFieldOptionValue(fieldDefinition, resolvedValue) ||
          resolvedValue;
        const matchingExtractionToolOption = getDropdownFieldOptions(
          fieldDefinition,
        ).find((option) =>
          doesDropdownOptionMatchValue(option, resolvedExtractionToolValue),
        );
        const selectedExtractionToolLabel = matchingExtractionToolOption
          ? getDropdownFieldOptionLabel(matchingExtractionToolOption)
          : resolvedValue;

        if (
          otherExtractionToolFieldKey &&
          !isOtherExtractionToolTriggerValue(selectedExtractionToolLabel) &&
          !isOtherExtractionToolTriggerValue(resolvedExtractionToolValue)
        ) {
          setFieldValueInSnapshot(otherExtractionToolFieldKey, "");
          clearFieldErrors([otherExtractionToolFieldKey]);
        }
      }

      if (fieldDefinition && isCancellationReasonField(fieldDefinition)) {
        const otherCancellationReasonField = Array.from(
          fieldDefinitionsMap.values(),
        ).find((candidateField) => {
          if (!isOtherCancellationReasonField(candidateField)) {
            return false;
          }

          if (
            fieldDefinition?.sectionName &&
            candidateField?.sectionName &&
            candidateField.sectionName !== fieldDefinition.sectionName
          ) {
            return false;
          }

          if (
            fieldDefinition?.rowKey &&
            candidateField?.rowKey &&
            candidateField.rowKey !== fieldDefinition.rowKey
          ) {
            return false;
          }

          return true;
        });
        const otherCancellationReasonFieldKey = getFieldStateKey(
          otherCancellationReasonField,
        );
        const resolvedCancellationReasonValue =
          resolveFieldOptionValue(fieldDefinition, resolvedValue) ||
          resolvedValue;
        const matchingCancellationReasonOption = getDropdownFieldOptions(
          fieldDefinition,
        ).find((option) =>
          doesDropdownOptionMatchValue(option, resolvedCancellationReasonValue),
        );
        const selectedCancellationReasonLabel = matchingCancellationReasonOption
          ? getDropdownFieldOptionLabel(matchingCancellationReasonOption)
          : resolvedValue;

        if (
          otherCancellationReasonFieldKey &&
          !isOtherLikeOptionValue(selectedCancellationReasonLabel) &&
          !isOtherLikeOptionValue(resolvedCancellationReasonValue)
        ) {
          setFieldValueInSnapshot(otherCancellationReasonFieldKey, "");
          clearFieldErrors([otherCancellationReasonFieldKey]);
        }
      }

      // Validate the field with updated values
      validateField(fieldKey, resolvedValue, dataType, updatedFieldValues);

      // If this is a contributor field, re-validate other contributor fields
      // to clear any resolved duplicate errors
      if (isContributorField(baseFieldKey)) {
        CONTRIBUTOR_UNIQUE_FIELDS.forEach((otherField) => {
          if (otherField !== baseFieldKey) {
            const otherFieldEntry = updatedFieldValues.find(
              (f) => f.fieldName === otherField,
            );
            if (otherFieldEntry?.updatedValue) {
              validateField(
                otherField,
                otherFieldEntry.updatedValue,
                "Text",
                updatedFieldValues,
              );
            }
          }
        });
      }

      // If this is a date field, re-validate all other date fields
      // to check cross-field date relationships
      if (dataType === "Date" && isDateRelationshipField(baseFieldKey)) {
        const allDateFields = Object.values(DATE_FIELDS);
        allDateFields.forEach((otherDateField) => {
          if (
            otherDateField !== baseFieldKey &&
            fieldDefinitionsMap.has(otherDateField)
          ) {
            validateField(
              otherDateField,
              // otherValue,
              getFieldValueFromSnapshot(otherDateField, updatedFieldValues),
              "Date",
              updatedFieldValues,
            );
            // }
          }
        });
      }

      setFieldValues(updatedFieldValues);
    },
    [
      doesFieldMatchLocationCandidates,
      effortSectionRows,
      fieldDefinitionsMap,
      fieldSectionMap,
      fieldValues,
      findMatchedApplicationUsedHoursReasonEntry,
      getEffortContributorFieldKey,
      getFieldValueFromSnapshot,
      getLocationFieldStateKeyByCandidates,
      locationHierarchyLookupData,
      getSavingReasonOptionsForRow,
      getSectionRowFieldKey,
      sectionRowsByKey,
      upsertFieldValue,
      validateField,
    ],
  );

  // Renders appropriate input field based on data type and edit mode
  const renderFieldInput = (field) => {
    const fieldStateKey = getFieldStateKey(field);
    const baseFieldKey = getBaseFieldName(field);
    const hasApiError =
      errorFieldArray.includes(baseFieldKey) ||
      errorFieldArray.includes(fieldStateKey);
    const hasFieldValidationError = hasValidationError(fieldStateKey);
    const hasFieldError = hasFieldValidationError || hasApiError;

    // Use edited value from fieldValues array if exists, otherwise use original field value
    const fieldEntry = fieldValues.find(
      (item) => item.fieldName === fieldStateKey,
    );
    const rawValue = fieldEntry
      ? fieldEntry.updatedValue
      : field.fieldValue || "";
    const value = getResolvedFieldValue(field, rawValue);
    const usesNumericStepper = usesNumericStepperField(baseFieldKey);
    const resolvedDataType = usesNumericStepper ? "numerical" : field.dataType;
    const selectedDropdownValue =
      resolvedDataType === "dropdown"
        ? resolveFieldOptionValue(field, value)
        : value;
    const selectedDropdownLabel =
      resolvedDataType === "dropdown"
        ? getDropdownDisplayValue(field, selectedDropdownValue)
        : "";
    const isAdditionalDidMultiField = isAdditionalDidField(field);
    if (!isEditing) {
      if (isAdditionalDidField(field)) {
        const displayValues = normalizeListDisplayValue(value);

        if (displayValues.length > 1) {
          return (
            <div className="field-display field-display-multiline">
              {displayValues.map((item) => (
                <div key={item}>{item}</div>
              ))}
            </div>
          );
        }

        return <div className="field-display">{displayValues[0] || ""}</div>;
      }

      if (isDateLikeField(field)) {
        const formattedDate = formatDateForDisplay(value);
        return (
          <div className="field-display">{formattedDate || value || ""}</div>
        );
      }

      if (resolvedDataType === "dropdown") {
        return (
          <div className="field-display">
            {getDropdownDisplayValue(field, value) || ""}
          </div>
        );
      }

      return <div className="field-display">{value || ""}</div>;
    }

    const canEditField = isEditableFieldRegionalLead(field);

    if (!canEditField && isDateLikeField(field)) {
      const formattedDate = formatDateForEditDisplay(value);
      const disabledDateValue =
        formattedDate ||
        (isEmptyDateValue(value) ? EMPTY_DATE_PLACEHOLDER : value || "");

      return (
        <div className="field-display field-display-disabled">
          {disabledDateValue}
        </div>
      );
    }

    if (
      canEditField &&
      isDateLikeField(field) &&
      field.dataType !== "dropdown"
    ) {
      const dateValue = formatDateForInput(value);
      const isDateEmpty = !dateValue;

      return (
        <div
          className={`field-input-wrapper date-field-wrapper ${hasFieldError ? "has-error" : ""} ${isDateEmpty ? "date-empty" : ""}`}
        >
          {/*
          <input
            type="date"
            className={`field-input date-input ${hasFieldError ? "field-error" : ""} ${isDateEmpty ? "date-input-empty" : ""}`}
            value={dateValue}
            title={getInputTooltipTitle(dateValue)}
            ref={(element) => setDateInputRef(fieldStateKey, element)}
            onChange={(e) => handleFieldChange(field, e.target.value, "Date")}
            disabled={!canEditField}
          />
          */}
          <input
            type="date"
            className={`field-input date-input ${hasFieldError ? "field-error" : ""} ${isDateEmpty ? "date-input-empty" : ""}`}
            value={dateValue}
            title={getInputTooltipTitle(dateValue)}
            ref={(element) => setDateInputRef(fieldStateKey, element)}
            onClick={(event) => {
              try {
                event.currentTarget.showPicker?.();
              } catch (error) {}
            }}
            onFocus={(event) => {
              try {
                event.currentTarget.showPicker?.();
              } catch (error) {}
            }}
            onKeyDown={(event) => {
              if (!["Tab", "Shift", "Escape"].includes(event.key)) {
                event.preventDefault();
              }
            }}
            onChange={(e) => handleFieldChange(field, e.target.value, "Date")}
            disabled={!canEditField}
            inputMode="none"
          />
          <FieldErrorIcon
            fieldKey={fieldStateKey}
            hasValidationError={hasValidationError}
            getValidationErrorMessage={getValidationErrorMessage}
            errorFieldArray={errorFieldArray}
          />
        </div>
      );
    }

    switch (resolvedDataType) {
      case "dropdown":
        // For disabled dropdowns in edit mode, show as text display
        if (!canEditField) {
          return (
            <div className="field-display field-display-disabled">
              {getDropdownDisplayValue(field, value) || ""}
            </div>
          );
        }

        const dropdownOptions = getDropdownOptions(field);
        const dropdownPlacementClass =
          dropdownPlacementByFieldKey[fieldStateKey] || "bottom";

        return (
          <div>
            {/* Previous implementation kept the dropdown as a standalone form field.
            
            */}
            <div
              ref={(element) => setDropdownFieldRef(fieldStateKey, element)}
              onMouseDownCapture={() =>
                updateDropdownPlacement(fieldStateKey, dropdownOptions.length)
              }
              onFocusCapture={() =>
                updateDropdownPlacement(fieldStateKey, dropdownOptions.length)
              }
              className={`field-input-wrapper dropdown-field-wrapper dropdown-menu-${dropdownPlacementClass} ${hasFieldError ? "has-error" : ""}`}
            >
              <MotifFormField
                className={hasFieldError ? "field-error-dropdown" : ""}
              >
                <MotifSelect
                  className={`field-dropdown ${!selectedDropdownValue ? "field-dropdown-empty" : ""} ${hasFieldError ? "field-error" : ""}`}
                  value={selectedDropdownValue}
                  title={selectedDropdownLabel || undefined}
                  onChange={(e) => handleFieldChange(field, e)}
                  placeholder={`Select ${field.labelToShowOnUI}`}
                  disabled={!canEditField}
                  searchPlaceholder="Select"
                  filter
                >
                  {dropdownOptions.map((option, index) => {
                    const optionValue = getDropdownFieldOptionValue(option);
                    const optionLabel = getDropdownFieldOptionLabel(option);
                    return (
                      <MotifOption
                        key={optionValue || index}
                        value={optionValue}
                        title={optionLabel}
                        disabled={Boolean(option?.isDisabled)}
                      >
                        {optionLabel}
                      </MotifOption>
                    );
                  })}
                </MotifSelect>
              </MotifFormField>

              <FieldErrorIcon
                fieldKey={fieldStateKey}
                hasValidationError={hasValidationError}
                getValidationErrorMessage={getValidationErrorMessage}
                errorFieldArray={errorFieldArray}
              />
            </div>
          </div>
        );

      case "numerical":
        return (
          <div
            className={`field-input-wrapper ${hasFieldError ? "has-error" : ""}`}
          >
            <input
              // type="text"
              type={usesNumericStepper ? "number" : "text"}
              className={`field-input ${usesNumericStepper ? "field-input-stepper" : ""} ${hasFieldError ? "field-error" : ""}`}
              value={value}
              title={getInputTooltipTitle(value)}
              onChange={(e) =>
                handleFieldChange(field, e.target.value, "numerical")
              }
              onKeyDown={(event) => {
                if (
                  usesNumericStepper &&
                  shouldPreventNumericStepperKey(event)
                ) {
                  event.preventDefault();
                }
              }}
              onPaste={(event) => {
                if (
                  usesNumericStepper &&
                  shouldPreventNumericStepperPaste(
                    baseFieldKey,
                    event.clipboardData?.getData("text") || "",
                  )
                ) {
                  event.preventDefault();
                }
              }}
              disabled={!canEditField}
              inputMode="decimal"
              step="any"
              min={usesNumericStepper ? "0" : undefined}
              placeholder={field.validation?.message || "Enter number"}
            />
            <FieldErrorIcon
              fieldKey={fieldStateKey}
              hasValidationError={hasValidationError}
              getValidationErrorMessage={getValidationErrorMessage}
              errorFieldArray={errorFieldArray}
            />
          </div>
        );
      case "ActiveDirectory":
        return (
          <div
            className={`field-input-wrapper active-directory-field-wrapper ${hasFieldError ? "has-error" : ""}`}
          >
            <ActiveDirectoryUserSearch
              value={normalizeListDisplayValue(value)}
              onChange={(emailOrEmails) =>
                handleFieldChange(field, emailOrEmails, "activeDirectory")
              }
              onPendingSelectionChange={(hasPendingSelection) => {
                if (
                  isContributorField(baseFieldKey) || isAdditionalDidMultiField
                ) {
                  setContributorSelectionPending(
                    fieldStateKey,
                    hasPendingSelection,
                  );
                }
              }}
              disabled={!canEditField}
              placeholder={`Search for ${field.labelToShowOnUI}...`}
              hasError={hasFieldError}
              fieldKey={baseFieldKey}
              allowMultiple={isAdditionalDidMultiField}
            />
            <FieldErrorIcon
              fieldKey={fieldStateKey}
              hasValidationError={hasValidationError}
              getValidationErrorMessage={getValidationErrorMessage}
              errorFieldArray={errorFieldArray}
            />
          </div>
        );

      case "text":
        return (
          <div
            className={`field-input-wrapper ${hasFieldError ? "has-error" : ""}`}
          >
            <input
              type="text"
              className={`field-input ${hasFieldError ? "field-error" : ""}`}
              value={value}
              title={getInputTooltipTitle(value)}
              onChange={(e) => handleFieldChange(field, e.target.value, "text")}
              disabled={!canEditField}
              placeholder={
                isDateLikeField(field)
                  ? EMPTY_DATE_PLACEHOLDER
                  : `Enter ${field.labelToShowOnUI}`
              }
            />
            <FieldErrorIcon
              fieldKey={fieldStateKey}
              hasValidationError={hasValidationError}
              getValidationErrorMessage={getValidationErrorMessage}
              errorFieldArray={errorFieldArray}
            />
          </div>
        );

      default:
        return (
          <div
            className={`field-input-wrapper ${hasApiError ? "has-error" : ""}`}
          >
            <input
              type="text"
              className={`field-input ${hasApiError ? "field-error" : ""}`}
              value={value}
              title={getInputTooltipTitle(value)}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              disabled={!canEditField}
              placeholder={
                isDateLikeField(field)
                  ? EMPTY_DATE_PLACEHOLDER
                  : `Enter ${field.labelToShowOnUI}`
              }
            />
            <FieldErrorIcon
              fieldKey={fieldStateKey}
              hasValidationError={hasValidationError}
              getValidationErrorMessage={getValidationErrorMessage}
              errorFieldArray={errorFieldArray}
            />
          </div>
        );
    }
  };

  // Toggles between edit and view mode
  const handleEditToggle = useCallback(async () => {
    if (isEditing) {
      setErrorFieldArray([]);
      clearErrorToast();

      if (activeTab === ORDER_TABS.CHECKLIST) {
        setIsSaving(true);
        try {
          const checklistSubmitted =
            (await checklistRef.current?.()) ?? false;

          if (!checklistSubmitted) {
            setIsSaving(false);
            return;
          }

          setIsEditing(false);
          setIsSaving(false);
          return;
        } catch (error) {
          console.error("Failed to submit checklist:", error);
          setErrorToastMessage(
            error?.message || DEFAULT_ORDER_UPDATE_ERROR_MESSAGE,
          );
          setErrorToastDetails([]);
          setShowErrorToast(true);
          setIsSaving(false);
          return;
        }
      }

      const validationResult = validateEditableFieldsOnSave();

      if (!validationResult.isValid) {
        revealValidationContext(validationResult.invalidFieldKeys);
        setErrorToastMessage(
          getValidationErrorToastMessage(validationResult.invalidFieldKeys),
        );
        setShowErrorToast(true);
        return;
      }

      setIsSaving(true);
      try {
        const fieldsToSave = fieldValues.map((field) => ({
          ...field,
          updatedValue: getFieldValueForSave(
            field.fieldName,
            field.updatedValue,
          ),
        }));

        const getSectionRowFieldValueForSave = (
          row,
          candidates = [],
          fallbackValue = "",
          options = {},
        ) => {
          const { preferRowDataWhenFieldMissing = false } = options;
          const fieldKey = getSectionRowFieldKey(row, candidates);
          const missingValueMarker = null;

          if (fieldKey) {
            const fieldValue = getSnapshotFieldValueForSave(
              fieldKey,
              fieldsToSave,
              preferRowDataWhenFieldMissing
                ? missingValueMarker
                : fallbackValue,
            );

            if (
              !preferRowDataWhenFieldMissing ||
              !(
                fieldValue === "" ||
                fieldValue === missingValueMarker ||
                fieldValue === undefined
              )
            ) {
              return fieldValue;
            }
          }

          if (preferRowDataWhenFieldMissing) {
            const rowDataValue = getRowDataValueByCandidates(
              row?.rowDataInfo,
              candidates,
              missingValueMarker,
            );

            if (
              rowDataValue !== "" &&
              rowDataValue !== missingValueMarker &&
              rowDataValue !== undefined
            ) {
              return rowDataValue;
            }
          }

          return fallbackValue;
        };

        const getStringPayloadIdentifier = (value) => {
          if (value === undefined || value === null || value === "") {
            return "";
          }

          return String(value);
        };

        const efforts = hasEffortSection
          ? effortSectionRows.map((row) => ({
              EffortForId: getStringPayloadIdentifier(
                getSectionRowFieldValueForSave(
                  row,
                  EFFORT_PAYLOAD_FIELD_CANDIDATES.effortForId,
                  "",
                  { preferRowDataWhenFieldMissing: true },
                ),
              ),
              effortHours: getSectionRowFieldValueForSave(
                row,
                EFFORT_PAYLOAD_FIELD_CANDIDATES.effortHours,
                "",
              ),
              role: getSectionRowFieldValueForSave(
                row,
                EFFORT_ROW_FIELD_CANDIDATES.role,
                "",
              ),
              resourceName: getSectionRowFieldValueForSave(
                row,
                EFFORT_ROW_FIELD_CANDIDATES.resourceName,
                "",
              ),
              reason: getSectionRowFieldValueForSave(
                row,
                EFFORT_PAYLOAD_FIELD_CANDIDATES.reason,
                "",
              ),
              effortForMasterId: getSectionRowFieldValueForSave(
                row,
                EFFORT_PAYLOAD_FIELD_CANDIDATES.effortForMasterId,
                "",
              ),
            }))
          : [];

        // Existing savings are deleted after save when Analyzer Type changes.
        const savings = hasSavingSection && !isAnalyzerTypeChanged
          ? savingSectionRows.map((row) => ({
              ID: getStringPayloadIdentifier(
                getSectionRowFieldValueForSave(
                  row,
                  SAVING_PAYLOAD_FIELD_CANDIDATES.id,
                  "",
                  { preferRowDataWhenFieldMissing: true },
                ),
              ),
              hoursToSave: getSectionRowFieldValueForSave(
                row,
                SAVING_PAYLOAD_FIELD_CANDIDATES.hoursToSave,
                "",
              ),
              applicationUsedId: getSectionRowFieldValueForSave(
                row,
                SAVING_PAYLOAD_FIELD_CANDIDATES.applicationUsedId,
                "",
              ),
              otherReason: getSectionRowFieldValueForSave(
                row,
                SAVING_ROW_FIELD_CANDIDATES.otherReason,
                "",
              ),
              reasonId: getSectionRowFieldValueForSave(
                row,
                SAVING_PAYLOAD_FIELD_CANDIDATES.reasonId,
                "",
              ),
              userName: getSectionRowFieldValueForSave(
                row,
                SAVING_ROW_FIELD_CANDIDATES.userName,
                userEmail,
              ),
            }))
          : [];

        console.log(
          "Orders/view effort rows with rowData:",
          effortSectionRows.map((row) => ({
            pagefieldsListInfo: row.pagefieldsListInfo,
            rowDataInfo: row.rowDataInfo,
          })),
        );
        console.log(
          "Orders/view saving rows with rowData:",
          savingSectionRows.map((row) => ({
            pagefieldsListInfo: row.pagefieldsListInfo,
            rowDataInfo: row.rowDataInfo,
          })),
        );

        const getFieldSectionNameForSave = (fieldName) => {
          const directSectionName =
            fieldSectionMap.get(fieldName) ||
            fieldDefinitionsMap.get(fieldName)?.sectionName ||
            "";

          if (directSectionName) {
            return directSectionName;
          }

          const baseFieldName = getBaseFieldName(fieldName);
          const matchesSectionRows = (rows) =>
            rows.some((row) =>
              row.fields.some(
                (rowField) => getBaseFieldName(rowField) === baseFieldName,
              ),
            );

          const matchesEffortRows = matchesSectionRows(effortSectionRows);
          const matchesSavingRows = matchesSectionRows(savingSectionRows);

          if (matchesEffortRows && !matchesSavingRows) {
            return EFFORT_SECTION_NAME;
          }

          if (matchesSavingRows && !matchesEffortRows) {
            return SAVING_SECTION_NAME;
          }

          return "";
        };

        const regularFieldsToSave = fieldsToSave
          .filter((field) => {
            const sectionName = getFieldSectionNameForSave(field.fieldName);

            if (
              sectionName === EFFORT_SECTION_NAME ||
              sectionName === SAVING_SECTION_NAME
            ) {
              return false;
            }

            const baseFieldName = getBaseFieldName(field.fieldName);
            const fieldDefinition =
              fieldDefinitionsMap.get(field.fieldName) ||
              fieldDefinitionsMap.get(baseFieldName);

            return shouldIncludeRegularFieldInPayload(fieldDefinition);
          })
          .map((field) => ({
            ...field,
            fieldName: getBaseFieldName(field.fieldName),
          }));

        // Call updateOrder API with the updated field values
        const payload = {
          requestId: headerData?.requestId?.toString() || "0",
          lastModifiedEmployeeEmailID:
            user?.email || user?.mail || user?.username || "",
          lastModifiedDate: new Date().toISOString(),
          currentStageId: 0,
          Role: currentUserRole,
          fields: regularFieldsToSave,
          efforts,
          savings,
          agentType: "UI",
        };
        const response = await OrdersService.updateOrder(payload);
        //  console.log("Update order response:", response);

        // Only update state if component is still mounted
        if (!isMountedRef.current) return;

        const validationWarnings = getUpdateOrderValidationWarnings(response);

        if (validationWarnings.length > 0) {
          setIsSaving(false);
          setErrorFieldArray([]);
          setErrorToastMessage(VALIDATION_WARNINGS_TOAST_MESSAGE);
          setErrorToastDetails(validationWarnings);
          setShowErrorToast(true);
          return;
        }

        if (isAnalyzerTypeChanged) {
          const existingSavingIds = (
            baseOtherSectionRowsBySection.get(SAVING_SECTION_NAME) || []
          )
            .filter((row) => !removedOtherSectionRowKeys.includes(row.rowKey))
            .map((row) =>
              getRowDataValueByCandidates(
                row?.rowDataInfo,
                SAVING_PAYLOAD_FIELD_CANDIDATES.id,
              ),
            )
            .filter((id) => id !== "" && id != null);

          if (existingSavingIds.length > 0) {
            try {
              await OrdersService.deleteSavingsList(
                headerData.requestId || headerData.orderId,
                existingSavingIds,
              );
            } catch (deleteError) {
              console.error("Failed to delete existing savings:", deleteError);

              if (isMountedRef.current) {
                setErrorToastMessage(
                  getApiErrorDisplayMessage(
                    deleteError,
                    "Order saved, but existing savings could not be deleted.",
                  ),
                );
                setErrorToastDetails([]);
                setShowErrorToast(true);
              }
            }

            if (!isMountedRef.current) return;
          }
        }

        setDraftOtherSectionRows({});
        nextDraftOtherSectionRowIdRef.current = 0;
        setIsAnalyzerTypeChanged(false);
        setShowAnalyzerTypeToast(false);
        setIsEditing(false);
        setIsSaving(false);
        // Refresh order details after successful update
        if (onRefreshOrderDetails) {
          onRefreshOrderDetails();
        }
      } catch (error) {
        // Only update state if component is still mounted
        if (!isMountedRef.current) return;

        console.error("Failed to update order:", error);

        const responseData = getApiErrorResponseData(error);
        const errorMessages = getApiErrorMessages(error);
        const errorFields =
          responseData?.fields || responseData?.errorFields || [];

        setErrorFieldArray(errorFields);

        if (errorMessages.length > 0) {
          setErrorToastMessage(DEFAULT_ORDER_UPDATE_ERROR_MESSAGE);
          setErrorToastDetails(errorMessages);
          setShowErrorToast(true);
          setIsSaving(false);
          return;
        }

        setErrorToastMessage(
          getApiErrorDisplayMessage(error, DEFAULT_ORDER_UPDATE_ERROR_MESSAGE),
        );
        setErrorToastDetails([]);
        setShowErrorToast(true);
        setFieldValues([]);
        setIsAnalyzerTypeChanged(false);
        setShowAnalyzerTypeToast(false);
        setDraftOtherSectionRows({});
        nextDraftOtherSectionRowIdRef.current = 0;
        setFieldValidationErrors({});
        setPendingContributorSelections({});

        setIsEditing(false);
        setIsSaving(false);
      }
    } else {
      setIsEditing(true);

      //---VALIDATION ON EDIT START---
      clearErrorToast();

      const validationResult = validateEditableFieldsOnSave();

      if (!validationResult.isValid) {
        revealValidationContext(validationResult.invalidFieldKeys);
        setErrorToastMessage(
          getValidationErrorToastMessage(validationResult.invalidFieldKeys),
        );
        setErrorToastDetails([]);
        setShowErrorToast(true);
      }
      //---VALIDATION ON EDIT END---
    }
  }, [
    activeTab,
    isEditing,
    isAnalyzerTypeChanged,
    baseOtherSectionRowsBySection,
    removedOtherSectionRowKeys,
    effortSectionRows,
    fieldDefinitionsMap,
    fieldSectionMap,
    headerData,
    fieldValues,
    getFieldValueForSave,
    getSnapshotFieldValueForSave,
    getSectionRowFieldKey,
    hasEffortSection,
    hasSavingSection,
    getValidationErrorToastMessage,
    onRefreshOrderDetails,
    revealValidationContext,
    savingSectionRows,
    shouldIncludeRegularFieldInPayload,
    user,
    userEmail,
    validateEditableFieldsOnSave,
    clearErrorToast,
  ]);

  // Show loading state
  if (isOrderLoading) {
    return (
      <div className="orders-dashboard">
        <LoadingIndicator show={true} fullscreen={false} />
      </div>
    );
  }

  // Show empty state if no order details
  if (!orderDetails || orderDetails.length === 0) {
    return (
      <div className="orders-dashboard">
        <div className="orders-empty-state">
          <p>No order details available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-dashboard">
      {/* Saving overlay */}
      {isSaving && <LoadingIndicator show={true} fullscreen={false} />}

      {/* Error toast message */}
      {showErrorToast && (
        <div className="error-toast">
          <div className="error-toast-message">
            <WarningCircle width="16" height="16" color={ERROR_COLOR} />{" "}
            <div className="error-toast-message-content">
              <span>{errorToastMessage}</span>
              {errorToastDetails.length > 0 && (
                <ul className="error-toast-message-list">
                  {errorToastDetails.map((warning, index) => (
                    <li key={`${warning}-${index}`}>{warning}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <button className="error-toast-close" onClick={clearErrorToast}>
            ×
          </button>
        </div>
      )}
      {showAnalyzerTypeToast &&
        createPortal(
          <div className="analyzer-type-info-toast" role="status">
            <InfoCircleIcon width="16" height="16" color="#188CE5" />
            <span>
              Analyser type has been changed, please save/cancel to proceed
              further
            </span>
            <button
              className="analyzer-type-info-toast-close"
              aria-label="Close"
              onClick={() => setShowAnalyzerTypeToast(false)}
            >
              ×
            </button>
          </div>,
          document.body,
        )}
      {/* Header section with order info, status, and dates */}
      <div className="ordersDetails-dashboard-header">
        <div className="header-top">
          {/* Order title with priority, ID, client, and analyzer */}
          <div className="header-top-heading">
            {headerData.priority && (
              <span className="header-top-heading-priority">
                {headerData.priority}
              </span>
            )}
            <span className="header-top-heading-name">
              {headerData.axonId && `AX-${headerData.axonId}`} |{" "}
              {(() => {
                const fullText =
                  headerData.engagmentName +
                  (headerData.clientEntity?.toLowerCase() !== "to be updated"
                    ? " - " + headerData.clientEntity
                    : "");
                const truncatedText =
                  fullText.length > 30
                    ? fullText.substring(0, 30) + "..."
                    : fullText;
                return fullText.length > 30 ? (
                  <CustomTooltipBottom
                    normalText={fullText}
                    trigger={truncatedText}
                  />
                ) : (
                  fullText
                );
              })()}{" "}
              | {headerData.analyzer || ""}
            </span>
          </div>
          {/* Status and date information */}
          <div className="header-top-content">
            <span className="header-top-content-status">
              <div className="header-top-content-status-label">
                {ORDER_DETAILS.STATUS_LABEL}
              </div>
              <span className="header-top-content-status-data">
                {headerData.orderActivityType === "Active"
                  ? headerData.currentSubStage
                  : headerData.orderActivityType}
                {headerData.agentType?.toLowerCase() === "outlook_agent" &&
                  headerData.actionNeeded === false && (
                    <span className="outlook-agent-icon">
                      <OutlookplusIcon />
                    </span>
                  )}
              </span>
            </span>
            <div className="order-item-dates">
              {parseInt(headerData.agingIn.split("d")[0], 10) >= 2 && (
                <CustomFieldsTooltip
                  trigger={
                    <p>
                      <CalendarIcon width="16" height="16" color="#A4A3B1" />
                      {headerData.agingIn || "1d"}
                    </p>
                  }
                  agingDays={headerData.agingIn || 0}
                  dueDate={formatDateShort(headerData.dueDateString)}
                  agingLabel={ORDER_DETAILS.AGEING_LABEL}
                />
              )}

              <CustomFieldsTooltip
                trigger={
                  <p>
                    <ClockIcon width="16" height="16" color="#A4A3B1" />
                    {formatDateShort(headerData.dueDateString)}
                  </p>
                }
                agingDays={headerData.agingIn || 0}
                dueDate={formatDateShort(headerData.dueDateString)}
                agingLabel={ORDER_DETAILS.DUE_DATE_LABEL}
              />
            </div>
          </div>
        </div>
        {/* Progress stepper component */}
        <OrderProgressStepper
          currentStatus={headerData.currentSubStage || ""}
        />

        {/* Main Tab Navigation - Order Info / Files */}
        <div className="orders-details-tabs-container">
          <div className="orders-details-tabs">
            <button
              className={`orders-details-tab ${activeMainTab === ORDER_DETAILS_MAIN_TABS.ORDER_INFO ? "active" : ""}`}
              onClick={() =>
                setActiveMainTab(ORDER_DETAILS_MAIN_TABS.ORDER_INFO)
              }
            >
              {ORDER_DETAILS_MAIN_TABS.ORDER_INFO}
            </button>
            <button
              className={`orders-details-tab ${activeMainTab === ORDER_DETAILS_MAIN_TABS.FILES ? "active" : ""} ${!isFilesTabAvailable || isAILoading ? "disabled" : ""}`}
              onClick={handleFilesTabClick}
              disabled={
                !isFilesTabAvailable ||
                isAILoading ||
                headerData.orderActivityType !== "Active"
              }
              title={
                isAILoading
                  ? "Please wait for AI summary to load"
                  : isFilesTabRestrictedCountry
                    ? "Data Triage is not available for orders in this country"
                    : !isGeneralLedgerAnalyzer
                      ? "Files tab is available only for General Ledger Analyzer orders"
                      : !isDataReceivedStatus
                        ? "Files tab is available only when order status is Data Received"
                        : headerData.orderActivityType !== "Active"
                          ? "Files tab is available only for active orders"
                          : ""
              }
            >
              {ORDER_DETAILS_MAIN_TABS.FILES}
            </button>
          </div>
        </div>
      </div>

      {/* Conditional rendering based on main tab selection */}
      <div className="orders-details-tab-content">
        {activeMainTab === ORDER_DETAILS_MAIN_TABS.FILES ? (
          <OrderDetailsFileUpload
            onDataReceivedFlowChange={onDataReceivedFlowChange}
            onIncompleteData={onIncompleteData}
          />
        ) : (
          /* Body section with tabs and editable fields */
          <div className="ordersDetails-dashboard-body">
            {/* Tab navigation and edit controls */}
            <div className="details-tabs-container">
              {/* Tab navigation buttons */}
              <div className="details-tabs-left">
                <div className="details-tabs">
                  <button
                    className={`tab ${activeTab === ORDER_TABS.MAIN_DETAILS ? "active" : ""} ${tabsWithErrors.has(ORDER_TABS.MAIN_DETAILS) ? "has-error" : ""}`}
                    onClick={() => handleSubTabChange(ORDER_TABS.MAIN_DETAILS)}
                  >
                    {ORDER_TABS.MAIN}
                  </button>
                  {showAllTabs && (
                    <button
                      className={`tab ${activeTab === ORDER_TABS.ADDITIONAL ? "active" : ""} ${tabsWithErrors.has(ORDER_TABS.ADDITIONAL) ? "has-error" : ""}`}
                      onClick={() => handleSubTabChange(ORDER_TABS.ADDITIONAL)}
                    >
                      {ORDER_TABS.ADDITIONAL}
                    </button>
                  )}
                  <button
                    className={`tab ${activeTab === ORDER_TABS.DATES ? "active" : ""} ${tabsWithErrors.has(ORDER_TABS.DATES) ? "has-error" : ""}`}
                    onClick={() => handleSubTabChange(ORDER_TABS.DATES)}
                  >
                    {ORDER_TABS.DATES}
                  </button>
                  <button
                    className={`tab ${activeTab === ORDER_TABS.CONTRIBUTORS ? "active" : ""} ${tabsWithErrors.has(ORDER_TABS.CONTRIBUTORS) ? "has-error" : ""}`}
                    onClick={() => handleSubTabChange(ORDER_TABS.CONTRIBUTORS)}
                  >
                    {ORDER_TABS.CONTRIBUTORS}
                  </button>
                  {showAllTabs && (
                    <button
                      className={`tab ${activeTab === ORDER_TABS.OTHER ? "active" : ""} ${tabsWithErrors.has(ORDER_TABS.OTHER) ? "has-error" : ""}`}
                      onClick={() => handleSubTabChange(ORDER_TABS.OTHER)}
                    >
                      {ORDER_TABS.OTHER}
                    </button>
                  )}
                  {showAllTabs && (
                    <button
                      className={`tab ${activeTab === ORDER_TABS.CHECKLIST ? "active" : ""} ${tabsWithErrors.has(ORDER_TABS.CHECKLIST) ? "has-error" : ""} ${!isChecklistEnabled ? "disabled" : ""}`}
                      onClick={() => {
                        if (isChecklistEnabled) {
                          handleSubTabChange(ORDER_TABS.CHECKLIST);
                        }
                      }}
                      disabled={!isChecklistEnabled}
                    >
                      {ORDER_TABS.CHECKLIST}
                    </button>
                  )}
                </div>
              </div>
              {/* Show all toggle and edit button */}
              <div className="details-tabs-right">
                <button
                  className={`show-all-button `}
                  onClick={() => {
                    if (
                      showAllTabs &&
                      (activeTab === ORDER_TABS.ADDITIONAL ||
                        activeTab === ORDER_TABS.OTHER)
                    ) {
                      if (isAnalyzerTypeChanged) {
                        setShowAnalyzerTypeToast(true);
                        return;
                      }
                      // When clicking "Show Less" and on Additional or Other tab, switch to Main Details
                      setActiveTab(ORDER_TABS.MAIN_DETAILS);
                    }
                    setShowAllTabs((prevShowAllTabs) => !prevShowAllTabs);
                  }}
                >
                  {showAllTabs ? (
                    <MinusIcon width={16} height={16} color="#A4A3B1" />
                  ) : (
                    <PlusIcon width={16} height={16} color="#A4A3B1" />
                  )}{" "}
                  {showAllTabs
                    ? ORDER_DETAILS.SHOW_LESS
                    : ORDER_DETAILS.SHOW_ALL}
                </button>
                <button
                  className={`edit-save-button ${isEditing ? "save-mode" : ""} ${isSaveActionDisabled ? "disabled" : ""}`}
                  onClick={handleEditToggle}
                  disabled={isSaveActionDisabled}
                  title={
                    activeTab === ORDER_TABS.CHECKLIST &&
                    isProjectCompleteStage
                      ? "Checklist editing is unavailable for Project Complete orders"
                      : activeTab === ORDER_TABS.CHECKLIST &&
                    isChecklistReadOnlyRole
                      ? "Checklist editing is unavailable for Regional Lead and Approver roles"
                      : !isActiveOrder && !isEditing
                      ? "Editing is disabled for inactive orders"
                      : isEditing && hasBlockingValidationErrors
                        ? "Resolve validation errors before saving"
                        : ""
                  }
                >
                  {isEditing ? (
                    ORDER_DETAILS.SAVE
                  ) : (
                    <EditIcon
                      color={isActiveOrder ? "#A4A3B1" : "#656579"}
                      width={17}
                      height={17}
                    />
                  )}
                </button>
                {isEditing && (
                  <button
                    className="cancel-edit-button"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            <div className="order-info-sections-scroll">
              {/* Dynamic fields grid based on active tab - Now using tab components */}
              {activeTab === ORDER_TABS.MAIN_DETAILS && (
                <MainDetailsTab
                  currentTabFields={currentTabFields}
                  isEditing={isEditing}
                  fieldValues={fieldValues}
                  errorFieldArray={errorFieldArray}
                  fieldValidationErrors={fieldValidationErrors}
                  renderFieldInput={renderFieldInput}
                  hasValidationError={hasValidationError}
                  getValidationErrorMessage={getValidationErrorMessage}
                />
              )}

              {activeTab === ORDER_TABS.ADDITIONAL && (
                <AdditionalDetailsTab
                  currentTabFields={currentTabFields}
                  isEditing={isEditing}
                  fieldValues={fieldValues}
                  errorFieldArray={errorFieldArray}
                  fieldValidationErrors={fieldValidationErrors}
                  renderFieldInput={renderFieldInput}
                  hasValidationError={hasValidationError}
                  getValidationErrorMessage={getValidationErrorMessage}
                />
              )}

              {activeTab === ORDER_TABS.DATES && (
                <DatesTab
                  currentTabFields={currentTabFields}
                  isEditing={isEditing}
                  fieldValues={fieldValues}
                  errorFieldArray={errorFieldArray}
                  fieldValidationErrors={fieldValidationErrors}
                  renderFieldInput={renderFieldInput}
                  hasValidationError={hasValidationError}
                  getValidationErrorMessage={getValidationErrorMessage}
                />
              )}

              {activeTab === ORDER_TABS.CONTRIBUTORS && (
                <ContributorsTab
                  currentTabFields={currentTabFields}
                  isEditing={isEditing}
                  fieldValues={fieldValues}
                  errorFieldArray={errorFieldArray}
                  fieldValidationErrors={fieldValidationErrors}
                  renderFieldInput={renderFieldInput}
                  hasValidationError={hasValidationError}
                  getValidationErrorMessage={getValidationErrorMessage}
                />
              )}

              {activeTab === ORDER_TABS.OTHER && (
                <OtherTab
                  currentTabSections={currentTabSections}
                  isEditing={isEditing}
                  expandedSections={expandedSections}
                  onToggleSectionExpansion={toggleSection}
                  renderFieldInput={renderFieldInput}
                  errorFieldArray={errorFieldArray}
                  onAddRow={handleAddOtherSectionRow}
                  onRemoveRow={handleRemoveOtherSectionRow}
                />
              )}

              {activeTab === ORDER_TABS.CHECKLIST && (
                <CheckListTab
                  submitRef={checklistRef}
                  resetRef={checklistResetRef}
                  currentTabFields={currentTabFields}
                  isEditing={isEditing && !isChecklistEditLocked}
                  fieldValues={fieldValues}
                  errorFieldArray={errorFieldArray}
                  fieldValidationErrors={fieldValidationErrors}
                  renderFieldInput={renderFieldInput}
                  hasValidationError={hasValidationError}
                  getValidationErrorMessage={getValidationErrorMessage}
                  orderId={headerData.requestId || headerData.orderId}
                  analyser={headerData.analyzerType || headerData.analyzer}
                  analyzerType={headerData.analyzerType || headerData.analyzer}
                  status={headerData.currentSubStage || headerData.status}
                  checklistRole={checklistRole}
                  accordionTitle={checklistAccordionTitle}
                />
              )}
            </div>

            {/* Contributors tab disclaimer - shown during edit mode */}
            {/* { activeTab === ORDER_TABS.CONTRIBUTORS && (
              <div className="contributors-disclaimer">
                <InfoCircleIcon width="16" height="16" color="#dedee2" />
                <span>
                  When adding any team member, regardless of role, remind them to comply with local independence policies before accessing a solution or agent, and complete the required independence declaration in EY Canvas (or equivalent, if applicable).
                </span>
              </div>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersDetailsDashboard;
